<template>
  <!-- Fixed 168px in both modes so the bottom section keeps one height — a
     requirement that outranks the card's own 224px in Figma 7520:166158
     (whose rotation view is 307 tall vs the strip view's 288). The card is
     laid out to fit this budget rather than growing the section; the extra
     56px it needs in the spec is reclaimed from the card's own spacing
     (padding, gaps, tile size). -->
<!-- The rounded top corners and upward shadow live on the tools band
     (MobileTools/index.vue) — the white card directly under the arrow, in
     both the strip and rotation views (Figma 7671:240258 "Component 3"). -->
<section
  class="mobile-shape-panel w-full bg-white flex flex-col shrink-0 select-none"
  :class="pickerMode === 'rotation'
    ? 'rotation-mode h-[223px] overflow-hidden rounded-t-[var(--sheet-corner-radius)]'
    : 'h-[168px] overflow-hidden'"
>
    <!-- Currently Selected Banner (Figma 5222:223165).
         Steps aside while the rotation card is up (Figma 7675:162616): the
         card replaces the section below the arrow, so the banner, tabs and
         edit toolbar all collapse. This is done with CSS height+opacity on
         .rotation-mode (not v-show) so the collapse animates in step with
         the panel growing into the toolbar's space. -->
    <div
      class="currently-selected-banner w-full h-5 bg-[#313558] flex items-center justify-center gap-1.5 px-3"
    >
      <span class="text-[11px] font-semibold text-white/90 leading-[15px]">
        Currently Selected:
      </span>
      <span class="text-[11px] font-bold text-white leading-[15px] truncate max-w-[200px]">
        {{ currentShapeName }}
      </span>
    </div>

    <!-- Section Category Tabs (Figma 7586:164487) -->
    <nav
      class="section-tabs-strip w-full px-4 pt-1 border-b border-[#EEEEEA] overflow-x-auto no-scrollbar"
      aria-label="Shape categories"
    >
      <div class="flex items-center gap-4 min-w-max pb-0.5">
        <button
          v-for="tab in availableTabs"
          :key="tab.id"
          type="button"
          class="tab-btn text-[12px] leading-[14px] font-bold transition-colors focus:outline-none cursor-pointer"
          :class="activeTab === tab.id ? 'text-[#313558] active-tab' : 'text-[#9A9994] hover:text-[#555]'"
          :aria-selected="activeTab === tab.id"
          role="tab"
          @click="setTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>
    </nav>

    <Transition name="picker-swap" mode="out-in">
      <!-- Grouped Tile Scroller with Sticky Headers (Figma 7374:204715 & 7605:173263).
           Fixed 115px height for every tab and grid (Figma 7605:179044), so the
           tile area is always the same size on every tab. -->

      <div
        v-if="pickerMode === 'strip'"
        key="strip"
        class="strip-wrap relative w-full overflow-hidden pt-2 pb-2 mb-3 h-[115px]"
        :class="[
          { 'strip-wrap--45deg': patternId === '45-deg' && activeTab !== 'boxes' },
          { 'strip-wrap--can-scroll': canScroll },
        ]"
        :data-tab="activeTab"
      >
        <!-- Scroll Fade Overlays: only visible when the strip actually overflows horizontally -->
        <div v-if="canScroll" class="scroll-fade scroll-fade--left pointer-events-none" />
        <div v-if="canScroll" class="scroll-fade scroll-fade--right pointer-events-none" />

        <!-- Tiles + scroll indicator share a 99px area; the remaining 16px
             of the fixed 115px strip holds the indicator (Figma 7374:204715). -->
        <div class="strip-viewport relative w-full h-[99px] overflow-hidden">
      <div
        ref="scrollerRef"
        class="scroller-content h-full flex items-stretch overflow-x-auto overflow-y-hidden px-[15px] gap-6"
        @scroll.passive="onScrollerScroll"
      >
        <!-- Engraved Tiles Tab: 1x1 Group and 2x2 Group -->
        <template v-if="activeTab === 'engraved'">
          <!-- Group 1: 1' x 1' (9mm only) -->
          <div class="tile-group flex flex-col shrink-0 relative h-full">
            <!-- Figma 7605:173264 header — a single 18px row: the size label
                 plus a #EDEDEA chip, side by side. The previous stacked
                 two-line version was 34.6px, which pushed the tiles down and
                 squeezed them inside the fixed-height strip. -->
            <div class="tile-group-header flex flex-row items-center gap-[6px] h-[18px] shrink-0">
              <span class="text-[13px] leading-[16px] text-[#9A9994]">1' × 1'</span>
              <button
                v-if="isCurrentTabUnavailable"
                type="button"
                class="header-chip h-[18px] px-[7px] flex items-center rounded-full bg-[#EDEDEA] text-[11px] font-bold leading-[13px] text-[#6B6B72] cursor-pointer"
                :aria-label="`${unavailableLabel.prefix}${unavailableLabel.link} - show available grids`"
                @click="openAvailableGrids"
              >
                {{ unavailableLabel.prefix }}<span class="availability-link ml-[3px]">{{ unavailableLabel.link }}</span>
              </button>
              <span
                v-else
                class="header-chip h-[18px] px-[7px] flex items-center rounded-full bg-[#EDEDEA] text-[11px] font-bold leading-[13px] text-[#6B6B72]"
              >
                9mm only
              </span>
            </div>
            <div class="flex flex-1 gap-2 self-stretch items-end">
              <button
                v-for="shape in engraved1x1Choices"
                :key="shape.id"
                type="button"
                class="tile-btn"
                :class="{ active: isShapeActive(shape), 'tile-btn--unavailable': isCurrentTabUnavailable }"
                :style="getTileBtnStyle(shape)"
                :title="shape.label"
                :aria-label="shape.label"
                :disabled="isCurrentTabUnavailable"
                @click="selectShape(shape, $event)"
              >
                <span class="tile-btn-label">{{ shape.label }}</span>
                <span class="tile-btn-icon" :class="{ 'tile-btn-icon--rotated': isRotatedHalfCircle(shape) }" :style="getTileIconFootprintStyle(shape)">
                  <span class="tile-btn-icon-svg" :style="getTileIconStyle(shape)" v-html="getShapeInnerSvg(shape, getTileIconBoxSize(shape).w, getTileIconBoxSize(shape).h)" />
                </span>
              </button>
            </div>
          </div>

          <!-- Group 2: 2' x 2' (9mm or 18mm) -->
          <div class="tile-group flex flex-col shrink-0 relative h-full">
            <!-- Figma 7605:173264: same single 18px row, but this chip is
                 outlined (1px #313558, no fill) rather than the filled #EDEDEA
                 pill on the 1×1 group — it advertises that this group takes
                 both depths, so it is styled as a choice, not a fact. -->
            <div class="tile-group-header flex flex-row items-center gap-[6px] h-[18px] shrink-0">
              <span class="text-[13px] leading-[16px] text-[#9A9994]">2' × 2'</span>
              <span class="header-chip h-[18px] px-[7px] flex items-center rounded-full border border-[#313558] bg-transparent text-[11px] font-bold leading-[13px] text-[#313558]">
                9mm or 18mm
              </span>
            </div>
            <div class="flex flex-1 gap-2 self-stretch items-end">
              <button
                v-for="shape in engraved2x2Choices"
                :key="shape.id"
                type="button"
                class="tile-btn"
                :class="{ active: isShapeActive(shape), 'tile-btn--unavailable': isCurrentTabUnavailable }"
                :style="getTileBtnStyle(shape)"
                :title="shape.label"
                :aria-label="shape.label"
                :disabled="isCurrentTabUnavailable"
                @click="selectShape(shape, $event)"
              >
                <span class="tile-btn-label">{{ shape.label }}</span>
                <span class="tile-btn-icon" :class="{ 'tile-btn-icon--rotated': isRotatedHalfCircle(shape) }" :style="getTileIconFootprintStyle(shape)">
                  <!-- The strip lists the 9mm set, so tiles render the plain
                       engraved artwork here. The layered (18mm) preview is only
                       shown on the rotation card for the selected tile. -->
                  <span
                    class="tile-btn-icon-svg"
                    :style="getTileIconStyle(shape)"
                    v-html="getShapeInnerSvg(shape, getTileIconBoxSize(shape).w, getTileIconBoxSize(shape).h)"
                  />
                </span>
              </button>
            </div>
          </div>
        </template>

        <!-- Shapes Tab: Standard Tiles -->
        <template v-else-if="activeTab === 'shapes'">
          <div class="tile-group flex flex-col shrink-0 relative h-full">
            <div v-if="isCurrentTabUnavailable" class="tile-group-header flex flex-row items-center gap-[6px] h-[18px] shrink-0">
              <button
                type="button"
                class="text-[10px] font-medium text-[#9A9994] text-left cursor-pointer"
                :aria-label="`${unavailableLabel.prefix}${unavailableLabel.link} - show available grids`"
                @click="openAvailableGrids"
              >
                {{ unavailableLabel.prefix }}<span class="availability-link ml-[3px]">{{ unavailableLabel.link }}</span>
              </button>
            </div>
            <div class="flex flex-1 gap-2 self-stretch items-end">
              <button
                v-for="shape in standardShapeChoices"
                :key="shape.id"
                type="button"
                class="tile-btn"
                :class="{ active: isShapeActive(shape), 'tile-btn--unavailable': isCurrentTabUnavailable }"
                :style="getTileBtnStyle(shape)"
                :title="shape.label"
                :aria-label="shape.label"
                :disabled="isCurrentTabUnavailable"
                @click="selectShape(shape, $event)"
              >
                <span class="tile-btn-label">{{ shape.label }}</span>
                <span class="tile-btn-icon" :class="{ 'tile-btn-icon--rotated': isRotatedHalfCircle(shape) }" :style="getTileIconFootprintStyle(shape)">
                  <span class="tile-btn-icon-svg" :style="getTileIconStyle(shape)" v-html="getShapeInnerSvg(shape, getTileIconBoxSize(shape).w, getTileIconBoxSize(shape).h)" />
                </span>
              </button>
            </div>
          </div>
        </template>

        <!-- Printed Tab: Printed Felt Patterns -->
        <template v-else-if="activeTab === 'printed'">
          <div class="tile-group flex flex-col shrink-0 relative h-full">
            <div class="tile-group-header flex flex-row items-center gap-[6px] h-[18px] shrink-0">
              <span class="text-[12px] font-bold text-[#313558]">Printed Patterns</span>
              <button
                v-if="isCurrentTabUnavailable"
                type="button"
                class="text-[10px] font-medium text-[#9A9994] text-left cursor-pointer"
                :aria-label="`${unavailableLabel.prefix}${unavailableLabel.link} - show available grids`"
                @click="openAvailableGrids"
              >
                {{ unavailableLabel.prefix }}<span class="availability-link ml-[3px]">{{ unavailableLabel.link }}</span>
              </button>
            </div>
            <div class="flex flex-1 gap-2 self-stretch items-end">
              <button
                v-for="shape in printedChoices"
                :key="shape.id"
                type="button"
                class="tile-btn"
                :class="{ active: isShapeActive(shape), 'tile-btn--unavailable': isCurrentTabUnavailable }"
                :style="getTileBtnStyle(shape)"
                :title="shape.label"
                :aria-label="shape.label"
                :disabled="isCurrentTabUnavailable"
                @click="selectShape(shape, $event)"
              >
                <span class="tile-btn-label">{{ shape.label }}</span>
                <!-- printed-tile marks this group so the grey stroke rule for
                     plain line-art tiles skips it: printed patterns carry their
                     own artwork and must keep their original colors. -->
                <span class="tile-btn-icon printed-tile" :class="{ 'tile-btn-icon--rotated': isRotatedHalfCircle(shape) }" :style="getTileIconFootprintStyle(shape)">
                  <span class="tile-btn-icon-svg" :style="getTileIconStyle(shape)" v-html="getShapeInnerSvg(shape, getTileIconBoxSize(shape).w, getTileIconBoxSize(shape).h)" />
                </span>
              </button>
            </div>
          </div>
        </template>

        <!-- Boxes Tab (3D Grids) — single horizontal scroll strip of all 3D
     box thumbnails. Mirrors the Figma 7282-204094 swatch sheet but laid
     out as one row (the user prefers the flattened single-row layout
     over the 3-row SMALL/MEDIUM/LARGE split). The strip scrolls
     horizontally to surface all 22 3D shape variants (6 base +
     16 engraved) on a 394px-wide mobile viewport. -->
        <template v-else-if="activeTab === 'boxes'">
          <div class="boxes-tab-wrap flex flex-col shrink-0 relative h-full">
            <!-- Single 18px row (like the Engraved tab's header) so the
                 availability link does not stack a second line above the
                 tiles and push them past the strip's fixed 99px. -->
            <div v-if="isCurrentTabUnavailable" class="tile-group-header flex flex-row items-center gap-[6px] h-[18px] shrink-0">
              <button
                type="button"
                class="text-[10px] font-medium text-[#9A9994] text-left cursor-pointer"
                :aria-label="`${unavailableLabel.prefix}${unavailableLabel.link} - show available grids`"
                @click="openAvailableGrids"
              >
                {{ unavailableLabel.prefix }}<span class="availability-link ml-[3px]">{{ unavailableLabel.link }}</span>
              </button>
            </div>
            <!-- flex-1 + items-end: the row takes the height left after the
                 header and its tiles sit on the strip's bottom edge, exactly
                 like the Shapes/Engraved/Printed rows. -->
            <div class="boxes-tab flex flex-1 items-end overflow-x-auto overflow-y-hidden w-full min-h-0 gap-2">
              <button
                v-for="shape in boxChoices"
                :key="shape.id"
                type="button"
                class="tile-btn"
                :class="{ active: isShapeActive(shape), 'tile-btn--unavailable': isCurrentTabUnavailable }"
                :style="getTileBtnStyle(shape)"
                :title="shape.label"
                :aria-label="shape.label"
                :disabled="isCurrentTabUnavailable"
                @click="selectShape(shape, $event)"
              >
                <span class="tile-btn-label">{{ shape.label }}</span>
                <span class="tile-btn-icon" :class="{ 'tile-btn-icon--rotated': isRotatedHalfCircle(shape) }" :style="getTileIconFootprintStyle(shape)">
                  <!-- 3D shapes: use the desktop's 3D-perspective
                       thumbnail (front+top+side face) via <img> instead of
                       the components/Shapes/SVG/3d text-only fallback. -->
                  <img
                    v-if="get3DShapeSrc(shape)"
                    :src="get3DShapeSrc(shape)!"
                    :alt="shape.label"
                    class="tile-btn-3d-svg"
                    :style="{ ...getTileIconStyle(shape), width: `${getShapeDisplaySize(shape).w}px`, height: `${getShapeDisplaySize(shape).h}px` }"
                  >
                  <span
                    v-else
                    class="tile-btn-icon-svg"
                    :style="getTileIconStyle(shape)"
                    v-html="getShapeInnerSvg(shape, getTileIconBoxSize(shape).w, getTileIconBoxSize(shape).h)"
                  />
                </span>
              </button>
            </div>
          </div>
        </template>

        <!-- Wooden Slats Tab (Figma 7672:162318) -->
        <template v-else-if="activeTab === 'wood-slats'">
          <div class="tile-group flex flex-col shrink-0 relative h-full">
            <!-- Figma header row: "Wooden Slats" label + "5 wood grain
                 options" pill (cornerRadius 999, fill #EDEDEA, text
                 #6B6B72 at Montserrat 700/11px).

                 On a grid that can't host wood slats the tiles are disabled,
                 so the pill is replaced by the availability link - the same
                 treatment the Shapes / Engraved / Printed tabs get. -->
            <div class="flex items-center gap-[6px] pb-[6px]">
              <span class="text-[13px] font-bold text-[#9A9994]">Wooden Slats</span>
              <button
                v-if="isCurrentTabUnavailable"
                type="button"
                class="text-[10px] font-medium text-[#9A9994] text-left cursor-pointer"
                :aria-label="`${unavailableLabel.prefix}${unavailableLabel.link} - show available grids`"
                @click="openAvailableGrids"
              >
                {{ unavailableLabel.prefix }}<span class="availability-link ml-[3px]">{{ unavailableLabel.link }}</span>
              </button>
              <span
                v-else
                class="px-[6px] py-[2px] rounded-full bg-[#EDEDEA] text-[11px] font-bold text-[#6B6B72] leading-[13px]"
              >
                5 wood grain options
              </span>
            </div>
            <div class="flex flex-1 items-end gap-6">
              <button
                v-for="shape in woodSlatChoices"
                :key="shape.id"
                type="button"
                class="tile-btn wood-slat-tile"
                :class="{ active: isShapeActive(shape), 'tile-btn--unavailable': isCurrentTabUnavailable }"
                :title="shape.label"
                :aria-label="shape.label"
                :disabled="isCurrentTabUnavailable"
                @click="selectShape(shape, $event)"
              >
                <!-- Figma uses short size labels (2x4 / 2x8) at Montserrat
                     700/13.7px in #1E1E23 instead of the long catalog label. -->
                <span class="text-[13px] font-bold text-[#1E1E23] leading-[16px]">
                  {{ woodSlatShortLabel(shape) }}
                </span>
                <span
                  class="tile-btn-icon wood-slat-icon-box"
                  :style="woodSlatIconStyle(shape)"
                >
                  <ShapesWoodSlatPickerIcon
                    :selected="isShapeActive(shape)"
                    :disabled="isCurrentTabUnavailable"
                  />
                </span>
              </button>
            </div>
          </div>
        </template>
      </div>
        </div>

        <!-- Horizontal Scroll Indicator (Figma 7374:204715 "Scroll position").
             A #E0E0E0 track holding a #313558 thumb that slides as the strip
             scrolls, sitting in the 16px below the tile viewport.

             The thumb is a FIXED 40px, as the spec draws it (120×4 track,
             40×4 thumb), and travels the rest of the track: it starts flush
             with the section's left edge and reaches its right edge when
             the strip is scrolled to the end, showing there is nothing more
             to scroll.

             The track spans the SECTION's content area rather than the
             spec's fixed 120px, so the pill's position reads as the scroll
             position across the whole strip. It is inset by the scroller's
             own 15px padding, so the thumb lines up with the first and last
             tiles rather than with the section's outer edges. -->
        <div
          v-if="canScroll"
          class="strip-scrollbar pointer-events-none relative mt-1 h-[4px] mx-[15px] rounded-full bg-[#E0E0E0]"
          aria-hidden="true"
        >
          <div
            class="absolute top-0 h-full w-[40px] rounded-full bg-[#313558] transition-[left] duration-150 ease-out"
            :style="{ left: `${scrollThumb.left}px` }"
          />
        </div>
    </div>

    <!-- Tile Selection / Rotation Card (Figma 7675:162616)
         Replaces the shape strip when a rotatable shape is picked. -->
    <!-- The rounded top and shadow live on .mobile-shape-panel, which is the
         sheet body in both the strip and rotation views. -->
    <div
      v-else
      key="rotation"
      class="rotation-card-wrap relative w-full px-4 pt-[14px] pb-[15px] flex flex-col h-full"
      :class="{ 'printed-card': isPrintedPattern(gridStore.selectedShape?.prefix) }"
      role="dialog"
      aria-label="Tile rotation options"
    >
      <div class="flex items-center gap-3 shrink-0 h-[36px]">
        <!-- Figma 7372:211744 header: a 36×36 shape preview beside the name and
             its size description, with Back at the far right. -->
        <div class="w-[36px] h-[36px] flex items-center justify-center shrink-0">
          <span
            v-if="gridStore.selectedShape"
            :style="{ transform: `rotate(${headerPreviewRotation}deg)`, transformOrigin: 'center' }"
            v-html="getShapeInnerSvg(gridStore.selectedShape, headerPreviewBox.w, headerPreviewBox.h, ownArtOrient(gridStore.selectedShape))"
          />
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-[15px] font-bold text-[#202124] leading-[18px] truncate">
            {{ selectedShapeBaseName }}
          </div>
          <div class="text-[10px] font-medium text-[#716D73] leading-[12px] truncate">
            {{ selectedShapeSubtitle }}
          </div>
        </div>
        <button
          type="button"
          class="text-[13px] font-bold text-[#313558] hover:opacity-70 px-2 py-1 cursor-pointer"
          @click="backToStrip"
        >
          Back
        </button>
      </div>

      <!-- Figma 7372:211744 — Depth block at y=59: 13px label, then a 7px
           gap, then the 25px toggle. The 9px gap above it separates it from
           the header (which ends at y=50). -->
      <div v-if="hasDepthOptions" class="mt-[9px]">
        <span class="block text-[11px] leading-[13px] font-bold text-[#9A9994] pb-[7px]">Depth</span>
        <!-- Depth toggle (Figma 7372:211744) — a 115×25 pill with 51×19
             segments. Shown only for shapes that actually have both depths:
             the 2×2 engraved set and its layered (18mm) variants. 1×1 shapes
             are 9mm-only, so they get no row at all. Selecting 18mm also swaps
             the selected tile to its layered counterpart — see setDepth(). -->
        <div class="flex items-center gap-3">
          <div class="depth-toggle-capsule flex items-center p-[2px] bg-white border border-[#313558] rounded-full h-[25px] w-[115px]" role="radiogroup" aria-label="Tile depth">
            <button
              type="button"
              class="depth-segment flex-1 h-[19px] rounded-full text-[11px] font-bold leading-[14px] flex items-center justify-center transition-colors cursor-pointer"
              :class="selectedDepth === 9 ? 'bg-[#313558] text-white' : 'bg-transparent text-[#313558]'"
              role="radio"
              :aria-checked="selectedDepth === 9"
              @click="setDepth(9)"
            >
              9mm
            </button>
            <button
              type="button"
              class="depth-segment flex-1 h-[19px] rounded-full text-[11px] font-bold leading-[14px] flex items-center justify-center transition-colors cursor-pointer"
              :class="selectedDepth === 18 ? 'bg-[#313558] text-white' : 'bg-transparent text-[#313558]'"
              role="radio"
              :aria-checked="selectedDepth === 18"
              @click="setDepth(18)"
            >
              18mm
            </button>
          </div>
          <span class="text-[11px] leading-[13px] text-[#6B6B72]">
            {{ selectedDepth === 18 ? 'Two colors of felt. Top layer and bottom layer.' : 'One layer of felt, one color' }}
          </span>
        </div>
      </div>

      <!-- Figma 7675:162616 — Rotation label at y=59 (no depth) or y=113
           (with depth), a 7px gap, then the 60x60 options with a 10px gap
           between them and the degree label 4px below each tile.

           Without a Depth row the card has spare height, so the rotation
           options are pushed to the bottom of the card (mt-auto) and sit
           directly above the footer, instead of leaving a gap below them.
           With a Depth row the content fills the card exactly, so mt-auto
           resolves to nothing. -->
      <div :class="hasDepthOptions ? 'mt-[9px]' : 'mt-auto'">
        <span class="block text-[11px] leading-[13px] font-bold text-[#9A9994] pb-[7px]">Rotation</span>
        <div class="flex items-start gap-[10px]">
          <button
            v-for="deg in rotationAngles"
            :key="deg"
            type="button"
            class="rotation-option flex flex-col items-center gap-1 cursor-pointer focus:outline-none"
            :aria-label="`Rotate to ${deg} degrees`"
            @click="setRotation(deg)"
          >
            <div
              class="rotation-tile flex items-center justify-center w-[60px] h-[60px] rounded-md border-2 transition-all"
              :class="currentRotation === deg
                ? 'border-[#313558] bg-[#F0F0F0]'
                : 'border-[#E6E5E0] bg-white hover:border-[#CCC]'"
            >
              <span
                v-if="gridStore.selectedShape"
                class="flex items-center justify-center leading-none"
                :style="rotationPreviewStyle(deg)"
                v-html="getShapeInnerSvg(
                  previewVariantForAngle(deg).shape,
                  rotationPreviewBox(deg).w,
                  rotationPreviewBox(deg).h,
                  rotationPreviewOrient(deg),
                )"
              />
            </div>
            <span
              class="rotation-label text-[10px] leading-[11px] font-semibold transition-colors"
              :class="currentRotation === deg ? 'text-[#313558] font-bold' : 'text-[#716D73]'"
            >
              {{ deg }}°
            </span>
          </button>
        </div>
      </div>
    </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
import { LAYERED_ENGRAVED_SHAPES } from '~/composables/shapes/layeredEngraving'
import { isLayeredShape } from '~/shared/layeredEngraving'
import { isPrintedPattern } from '~/composables/shapes/printedPatterns'
import { ENGRAVED_1X1_SQUARE_SHAPES } from '~/composables/shapes/shapesEngraved1x1Square'
import { ENGRAVED_2X2_SQUARE_SHAPES } from '~/composables/shapes/shapesEngraved2x2Square'
import { GRID_SHAPES } from '~/composables/shapes/constants'
import { ENGRAVED_3D_SHAPES, SHAPES as SHAPES_FOR_3D } from '~/composables/shapes/shapesFor3D'
import { shouldShowRotationControl } from '~/composables/shapes/shapeCapabilities'
import { engravedBaseArtFlip } from '~/composables/shapes/engravedOrientation'
import { isWoodSlatShapeId, WOOD_SLAT_FREEFORM_SHAPES, WOOD_SLAT_SQUARE_SHAPES } from '~/composables/shapes/woodSlats'
import { createPickerInteractions } from '~/components/NewStudio/logic'
import { GRID_PATTERN_IDS, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import type { Shape } from '~/types/grid.types'

// Load the mobile shape SVGs (components/Shapes/SVG/**). The mobile 3D
// folder only contains text labels (depth "2"/"4") which don't match the
// Figma 7282-204094 spec — the spec shows 3D-perspective box
// illustrations. For 3D shapes we instead use the desktop studio's
// proper 3D-perspective SVGs (served at /new-studio/shapes/3d/** and
// /new-studio/shapes/3d-engraved/**) via a runtime <img> tag via
// get3DShapeSrc().
const svgModules = import.meta.glob('../../../components/Shapes/SVG/**/*.svg', { eager: true, query: '?raw', import: 'default' }) as Record<string, string>

// Map a 3D shape to the URL of the matching desktop 3D-perspective
// thumbnail (Figma 7282-204094). The desktop has 6 base shapes
// (Cube12x12x2/4, Cube36x36x2/4, Rect12x36x2/4) under /new-studio/shapes/3d/
// and 8 engraved variants under /new-studio/shapes/3d-engraved/ — only
// the 4" depth versions exist (the 2" depth uses the basic 3D folder).
const get3DShapeSrc = (shape: Shape): string | null => {
  const prefix = shape.prefix || ''
  if (prefix === 'Cube12x12x2') return '/new-studio/shapes/3d/Cube12x12x2.svg'
  if (prefix === 'Cube12x12x4') return '/new-studio/shapes/3d/Cube12x12x4.svg'
  if (prefix === 'Cube36x36x2') return '/new-studio/shapes/3d/Cube36x36x2.svg'
  if (prefix === 'Cube36x36x4') return '/new-studio/shapes/3d/Cube36x36x4.svg'
  if (prefix === 'Rect12x36x2') return '/new-studio/shapes/3d/Rect12x36x2.svg'
  if (prefix === 'Rect12x36x4') return '/new-studio/shapes/3d/Rect12x36x4.svg'
  // Engraved 3D variants: prefix = "Cube36x36x{2|4}{pattern}" (no separator).
  // Desktop only ships 4" engraved art; 2" reuses the 4" asset since the
  // pattern visual is identical.
  const variantMatch = prefix.match(/^Cube36x36x(\d)(Tangent|Zen|River|Offset|Topo|Converge|Subway|LargeParallel)$/)
  if (variantMatch) {
    const name = variantMatch[2]
    return `/new-studio/shapes/3d-engraved/${name}4.svg`
  }
  return null
}

const gridStore = useGridStore()
const userStore = useUserStore()
const interactions = useShapeInteractions()
const picker = createPickerInteractions(interactions, () => gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD))

const activeTab = ref<'shapes' | 'engraved' | 'printed' | 'boxes' | 'wood-slats'>('shapes')
const scrollerRef = ref<HTMLElement | null>(null)
// Picker mode: 'strip' shows the shape scroller, 'rotation' shows the
// Figma 7675:162616 Tile Selection card with rotation options.
const pickerMode = ref<'strip' | 'rotation'>('strip')

// Mirrored to the sheet so the edit toolbar (rendered in MobileTools/index.vue)
// can hide while the rotation card owns the section. See Figma 7675:162616.
const showEditToolbar = useState<boolean>('mobile-show-edit-toolbar', () => true)
// True while the tile-rotation card is up. The sheet sizes the picker band
// to match (see .sheet-band--picker-rotation in MobileTools/index.vue).
const rotationCardOpen = useState<boolean>('mobile-rotation-card-open', () => false)
watch(pickerMode, mode => {
  showEditToolbar.value = mode === 'strip'
  rotationCardOpen.value = mode === 'rotation'
})

// Custom horizontal scroll indicator (Figma 7374:204715 "Scroll position").
// The spec draws a 120×4 track with a 40×4 #313558 thumb that slides along it
// as the strip scrolls (rest x=0, scrolled 150px x=35, scrolled 300px x=69).
//
// Here the track spans the whole section instead of being a fixed 120px box,
// so the thumb reads as the scroll position across the strip: it starts
// flush with the section's LEFT edge and travels to its RIGHT edge when the
// strip is scrolled to the end, which shows there is nothing more to
// scroll. The thumb keeps the spec's fixed 40px width, so it is the same
// size on every tab and only its position changes.
const SCROLL_THUMB_WIDTH = 40
const scrollThumb = ref({ left: 0 })

// True only when the strip actually overflows horizontally. When the content
// fits on screen (Wooden Slats, short lists), the scroll indicator and the
// white scroll fades are hidden entirely — a visible pill there would imply
// content that isn't there.
const canScroll = ref(false)

const updateScrollThumb = () => {
  const el = scrollerRef.value
  if (!el) {
    canScroll.value = false
    return
  }
  const max = el.scrollWidth - el.clientWidth
  if (max <= 0) {
    // Nothing to scroll: the indicator is hidden entirely.
    canScroll.value = false
    scrollThumb.value = { left: 0 }
    return
  }
  canScroll.value = true
  // The thumb is a fixed 40px, so it travels the track minus its own
  // width. At scrollLeft = max it sits flush against the track's right
  // edge; at scrollLeft = 0 it sits flush against the left edge.
  const track = el.clientWidth - 30 /* the scroller's 15px padding each side */
  const travelPx = Math.max(0, track - SCROLL_THUMB_WIDTH)
  const leftPct = travelPx > 0 ? (el.scrollLeft / max) * travelPx : 0
  scrollThumb.value = { left: leftPct }
}

const onScrollerScroll = () => updateScrollThumb()

onMounted(() => {
  updateScrollThumb()
  window.addEventListener('resize', updateScrollThumb)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateScrollThumb)
})

// Re-measure when the tab or grid changes the tile list, and once the
// transition (picker-swap, 200ms opacity) has settled.
watch([activeTab, () => gridStore.gridPattern?.id], async () => {
  await nextTick()
  updateScrollThumb()
  setTimeout(updateScrollThumb, 240)
})

const patternId = computed(() => gridStore.gridPattern?.id || 'square')
const supportsLayered = computed(() => ['square', '45-deg', 'freeform'].includes(patternId.value))
const supportsEngraved = computed(() => ['square', '45-deg', 'freeform'].includes(patternId.value))
const is3DGrid = computed(() => patternId.value === '3d')

const availableTabs = computed(() => {
  // All five categories are always shown on every grid. Which *tiles* inside
  // each tab are pickable is decided per-shape by isShapeUnavailable(),
  // which mirrors the desktop's PANEL_SHAPE_GROUPS contract
  // (components/NewStudio/right-panel/panelContract.ts).
  const tabs: { id: 'shapes' | 'engraved' | 'printed' | 'boxes' | 'wood-slats'; label: string }[] = [
    { id: 'shapes', label: 'Shapes' },
    { id: 'engraved', label: 'Engraved' },
    { id: 'printed', label: 'Printed' },
    { id: 'boxes', label: 'Boxes' },
    { id: 'wood-slats', label: 'Wooden Slats' },
  ]
  // Wooden Slats is an internal catalogue: the desktop only renders the
  // wood-slats group for admin users (ShapePicker.vue drops every slat
  // shape for non-admins, and the group disappears once it is empty).
  // isAdmin is true only for @feltright.com accounts, so the mobile tab is
  // hidden for everyone else — including signed-in customers.
  if (!userStore.isAdmin) {
    return tabs.filter(tab => tab.id !== 'wood-slats')
  }
  return tabs
})

// If the Wooden Slats tab disappears (logout, or a non-admin session),
// fall back to the Shapes tab so the strip never renders behind a tab
// that isn't listed.
watch(availableTabs, (tabs) => {
  if (!tabs.some(tab => tab.id === activeTab.value)) {
    activeTab.value = 'shapes'
  }
})

const setTab = (tabId: 'shapes' | 'engraved' | 'printed' | 'boxes' | 'wood-slats') => {
  activeTab.value = tabId
  // Switching tabs should always drop back to the shape strip so the user
  // sees the tiles for the newly selected category (instead of staying in
  // a previously opened rotation card).
  pickerMode.value = 'strip'
  showEditToolbar.value = true
  if (scrollerRef.value) {
    scrollerRef.value.scrollLeft = 0
  }
}

const currentShapeName = computed(() => {
  return gridStore.selectedShape?.label || 'None'
})

// Engraved tile lists
const engraved1x1Choices = computed(() => ENGRAVED_1X1_SQUARE_SHAPES)

// The 45° grid carries its OWN engraved entries, and they are not the square
// ones: the 45° list's Tangent is `EngravedTangent45deg` with a rotation of
// 45, and its variants sit at 45 / 135 / 225 / 315.
//
// The Engraved tab used to list the SQUARE set on every grid, so on the 45°
// grid the tiles were described with the square grid's rotations (0/90/180/
// 270) and the square grid's artwork. The rotation card then showed the
// 0° artwork turned by each angle, which put the 90/180/270 options in the
// default (square-grid) orientation instead of the 45° one.
//
// Reading the list from the grid's own data fixes it: on the 45° grid the
// shapes carry the 45° rotations and the 45° artwork, so the card's options
// are described the same way the tiles that land on the grid are.
//
// The strip always lists the DEFAULT (9mm) 2×2 set. Depth is chosen on the
// rotation card (Figma 7372:211744) and applies to that one selected tile —
// it must not rewrite the strip. When the user taps Back to return here, they
// should land on the same list they left, not on the 18mm variants.
//
// Shapes flagged `admin` in the shape data (currently Knot) are internal
// previews and must not appear for customers. Gate them on isAdmin, which the
// user store sets for @feltright.com addresses — the same audience as the
// FR-67 layered-engraving restriction.
const engraved2x2Choices = computed(() => {
  const list = is45degGrid.value
    // The 45° grid's own engraved 2×2 entries.
    ? (GRID_SHAPES as any)['45-deg'].filter((s: Shape) =>
        s.prefix && ENGRAVED_2X2_PREFIXES.has(s.prefix))
    : ENGRAVED_2X2_SQUARE_SHAPES
  return list.filter((s: Shape) => !(s as any).admin || userStore.isAdmin)
})

// Standard shapes for grid
const standardShapeChoices = computed(() => {
  // The 3D grid's own list is the set of 3D BLOCKS (cubes and rects), and
  // those belong to the Boxes tab. Listing them here as well meant the
  // Shapes tab showed the same blocks greyed out - a duplicate of the Boxes
  // tab that could never be picked.
  //
  // The desktop's panel contract puts the square-grid shapes here instead,
  // as an unavailable "Shapes available on other grids" reference row
  // (panelContract.ts, the '3d' variant's 'other-grids' group). That is the
  // reference the mobile strip follows, so the 3D grid shows the 2D shapes
  // greyed out and the blocks live only in the Boxes tab.
  const list = (patternId.value === '3d')
    ? (GRID_SHAPES as any)['square'] || []
    : (GRID_SHAPES as any)[patternId.value] || []
  // Wood slats live in the same square/freeform lists but have their own
  // tab (see woodSlatChoices below) — they must not also show under Shapes.
  return list.filter((s: Shape) =>
    !isWoodSlatShapeId(s.id)
    && !s.id.includes('Engraved')
    && !s.id.includes('Aspen')
    && !s.id.includes('Bamboo')
    && !s.id.includes('Geo')
    && !s.id.includes('Pinstripe')
    && !s.id.includes('Blossom')
    && !s.id.includes('Strand')
  )
})

// Printed choices. On grids that don't have printed patterns (e.g. 3D),
// fall back to the square-grid printed patterns so the user still sees
// the 6 printed tiles (rendered greyed out via `isCurrentTabUnavailable`),
// matching the desktop panelContract.ts pattern.
const printedChoices = computed(() => {
  const currentList = (GRID_SHAPES as any)[patternId.value] || []
  const patternMatches = (s: Shape) =>
    s.id.includes('Aspen') || s.id.includes('Bamboo') || s.id.includes('Geo') ||
    s.id.includes('Pinstripe') || s.id.includes('Blossom') || s.id.includes('Strand')
  const matches = currentList.filter(patternMatches)
  if (matches.length > 0) return matches
  // Fallback to square-grid printed patterns for unavailable reference
  const squareList = (GRID_SHAPES as any)['square'] || []
  return squareList.filter(patternMatches)
})

// Box choices for 3D (per Figma 7282-204094 Mobile - Shapes (2-Row)).
// Single horizontal scroll strip showing every 3D box variant. The order
// matches the Figma swatch: smallest size first (12"×12"), then
// medium (12"×36"), then large (36"×36" + engraved), each with the 4"
// depth first then 2" — i.e. left-to-right as the user scrolls.
const BOX_TILE_ORDER = [
  'Cube12x12x4',
  'Cube12x12x2',
  'Rect12x36x4',
  'Rect12x36x2',
  'Cube36x36x4',
  'Cube36x36x2',
]
// Engraved variants: 4" then 2" for each pattern (Large Parallel, Tangent,
// Zen, River, Offset, Topo, Converge, Subway).
const ENGRAVED_VARIANT_ORDER = [
  'LargeParallel',
  'Tangent',
  'Zen',
  'River',
  'Offset',
  'Topo',
  'Converge',
  'Subway',
]

// Per-tab availability — mirrors the desktop's PANEL_SHAPE_GROUPS contract
// (components/NewStudio/right-panel/panelContract.ts), where each grid
// variant marks specific groups `unavailable: true`. We translate that into a
// per-tab flag:
//
//   Grid          Shapes  Engraved  Printed  Boxes  WoodSlats
//   square        ✓        ✓         ✓        ✗       ✓
//   45-square     ✓        ✓         ✓        ✗       ✗ (no group)
//   hex           ✓        ✗         ✗        ✗       ✗ (no group)
//   90-hex        ✓        ✗         ✗        ✗       ✗ (no group)
//   spaced-hex    ✓        ✗         ✗        ✗       ✗ (no group)
//   freeform      ✓        ✓         ✓        ✗       ✓
//   3d            ✗        ✗         ✗        ✓       ✗ (no group)
//
// The three 2D-grid-only groups (Engraved / Printed / Boxes) render their
// tiles greyed + disabled as reference previews, matching the desktop's
// "Shapes available on other grids" / "Shapes available in 3D Grid" labels.
// Tabs the desktop doesn't list at all for a grid (Wood Slats outside
// square/freeform) are hidden by availableTabs rather than greyed.
const UNSUPPORTED_PRINTED_GRIDS = new Set(['hex', 'hex-90', 'hex-spaced', '3d'])
const UNSUPPORTED_ENGRAVED_GRIDS = new Set(['hex', 'hex-90', 'hex-spaced', '3d'])

const isCurrentTabUnavailable = computed(() => {
  const id = activeTab.value
  const grid = patternId.value
  // Shapes (standard 2D tiles): available on every 2D grid; the 3D grid
  // replaces them with 3D blocks, so they're reference-only there.
  if (id === 'shapes') return grid === '3d'
  // Engraved 1×1 / 2×2 tiles: square + 45-square + freeform only.
  if (id === 'engraved') return UNSUPPORTED_ENGRAVED_GRIDS.has(grid)
  // Printed patterns: square + 45-square + freeform only.
  if (id === 'printed') return UNSUPPORTED_PRINTED_GRIDS.has(grid)
  // 3D block sets + 3D engraved variants: 3D grid only.
  if (id === 'boxes') return grid !== '3d'
  // Wood slats: square + freeform only.
  if (id === 'wood-slats') return !['square', 'freeform'].includes(grid)
  return false
})

// Label for the unavailable state — mirrors the desktop's
// `group.label` from panelContract.ts, which the desktop renders as
// `<p class="shape-picker__other-label">`. The Boxes tab on a non-3D
// grid gets the 3D-grid label; every other unavailable group gets the
// "other grids" label.
// The availability label, split into the plain prefix and the link word.
// The desktop renders "Shapes available on other [grids]" and "Shapes
// available in [3D Grid]" with just the link word in navy and underlined
// (see .shape-picker__grid-link in NewStudio/ShapePicker.vue). The mobile
// label follows the same treatment.
const unavailableLabel = computed(() => {
  if (activeTab.value === 'boxes') return { prefix: 'Shapes available in ', link: '3D Grid' }
  return { prefix: 'Shapes available on other ', link: 'grids' }
})

// --- "Available on other grids" link ----------------------------------------
// Mirrors the desktop: the label opens the Grid Options modal with the
// grids that support the current tab's shapes highlighted, so the user can
// jump straight to one. The desktop drives this through
// useNewStudioActions().gridOptions(); the mobile shell keeps the same
// shared state so both pickers stay in sync.
const showMobileGridOptions = useState<boolean>('new-studio-grid-options-open', () => false)
const highlightedGridIds = useState<string[]>('new-studio-highlighted-grids', () => [])

// The grid ids each tab's shapes support. The desktop derives this from the
// panel contract's shape groups; the mobile tabs have the same rule set,
// expressed in `isCurrentTabUnavailable` above.
const availableGridIdsForTab = computed<string[]>(() => {
  const id = activeTab.value
  if (id === 'boxes') return [GRID_PATTERN_IDS.THREE_D]
  if (id === 'shapes') {
    // Standard 2D tiles: every 2D grid.
    return [GRID_PATTERN_IDS.SQUARE, GRID_PATTERN_IDS.SQUARE_45, GRID_PATTERN_IDS.HEX,
      GRID_PATTERN_IDS.HEX_90, GRID_PATTERN_IDS.HEX_SPACED, GRID_PATTERN_IDS.FREEFORM]
  }
  if (id === 'wood-slats') return [GRID_PATTERN_IDS.SQUARE, GRID_PATTERN_IDS.FREEFORM]
  // Engraved and Printed: square + 45-square + freeform.
  return [GRID_PATTERN_IDS.SQUARE, GRID_PATTERN_IDS.SQUARE_45, GRID_PATTERN_IDS.FREEFORM]
})

const openAvailableGrids = () => {
  highlightedGridIds.value = availableGridIdsForTab.value
  showMobileGridOptions.value = true
}

// Available helpers for the Figma-correct ordering (kept as-is for the
// standard shapes / engraved / printed tabs).
const boxChoices = computed(() => {
  const blank = (GRID_SHAPES as any)['3d'] || []
  // Flatten so each rotation variant (rotation != 0) is a top-level entry
  // rather than a nested child. The mobile picker only needs the parent
  // shapes; rotation variants are surfaced through the rotation card.
  // Match by prefix (e.g. "Cube12x12x4") so the order is stable.
  const blanksByPrefix = new Map(
    blank
      .filter((s: Shape) => (s.rotation || 0) === 0)
      .map((s: Shape) => [s.prefix, s])
  )
  // Engraved variants — pick only the top-level (rotation 0) parent shape
  // per pattern+depth in Figma order.
  const engraved = ENGRAVED_3D_SHAPES.filter((s: Shape) => (s.rotation || 0) === 0)
  const blanksOrdered = BOX_TILE_ORDER
    .map(prefix => blanksByPrefix.get(prefix))
    .filter((s): s is Shape => Boolean(s))
  // Engraved variants: for each pattern, the 4" depth first then 2".
  const engravedOrdered = ENGRAVED_VARIANT_ORDER.flatMap((pattern) => {
    return [
      engraved.find(s => s.prefix === `Cube36x36x4${pattern}`),
      engraved.find(s => s.prefix === `Cube36x36x2${pattern}`),
    ]
  }).filter((s): s is Shape => Boolean(s))
  return [...blanksOrdered, ...engravedOrdered]
})


// Wood slats choices
const woodSlatChoices = computed(() => patternId.value === 'freeform' ? WOOD_SLAT_FREEFORM_SHAPES : WOOD_SLAT_SQUARE_SHAPES)

// Figma 7672:162318 short size labels (2x4 / 2x8) for the wood slat tiles
// instead of the long catalog label. The 4Ft variants show "2x4" and
// 8Ft variants show "2x8" — the visual spec used 2x4 because the wood slat
// is 2" wide x 4" tall. The Figma node is the source of truth here.
const woodSlatShortLabel = (shape: Shape): string => {
  if (shape.id.includes('4Ft')) return '2x4'
  if (shape.id.includes('8Ft')) return '2x8'
  return shape.label
}

// Wood slat icon dimensions per Figma 7672:162318. The wrapper reserves
// the Figma *landscape* footprint in layout (2x4 → 88×44, 2x8 → 175×44)
// so the flex row gives each tile the right width and the labels don't
// overlap. The painted slat is positioned inside, sized to the
// un-rotated portrait aspect, then rotated 90° about the centre. The
// slatImageW/H helpers set explicit width/height attributes on the
// picker <img> elements so they beat the SVG's intrinsic 52×104 size.
// Figma 7672:162318 footprint. The source SVGs draw two *standing* slats
// side by side (each ~25×102 inside a 52×104 viewBox); the spec shows them
// *lying down*, so the art is rotated 90°. The wrapper reserves the
// rotated (landscape) footprint so flex gives each tile the right width,
// and the inner icon is sized to the un-rotated portrait aspect, scaled
// and rotated to fill it.
// Wood slat wrapper footprint per Figma 7671:233687 (2x4 → 88×44, 2x8 →
// 175×44). --slat-w carries the wrapper's WIDTH, which the inner img's
// pre-rotation height matches so a 90° rotation fills the wrapper exactly.
const woodSlatIconStyle = (shape: Shape): Record<string, string> =>
  shape.id.includes('8Ft')
    ? { width: '175px', height: '44px', '--slat-w': '175px' }
    : { width: '88px',  height: '44px', '--slat-w': '88px' }

const getShapeComponent = (shape: Shape, orient?: 'vertical' | 'horizontal'): string | null => {
  let icon = shape.icon || shape.prefix || shape.id
  // The halflap/shiplap shape definitions use icon names that point to
  // the horizontal-orientation SVG file even when the shape is meant to
  // be displayed vertically. Detect this mismatch and swap the icon
  // name so the correct SVG file is loaded.
  //
  // The grid suffix has to survive the swap. `endsWith('Horizontal')` is
  // false for `ShiplapHorizontal45deg`, so the 45° grid kept loading the
  // horizontal 4:1 artwork for a vertical display size. That artwork
  // letterboxed inside the vertical box and painted a 4px band, while the
  // halflaps (whose 45° assets are already vertical) painted a 25px band.
  // The shiplaps read as hairlines next to the halflaps for that reason.
  //
  // `orient` overrides which way the art points. The rotation card passes
  // the orientation of the tile the CANVAS will place, because the card
  // previews the placed tile. The strip leaves it unset and follows the
  // display size instead.
  const suffixMatch = icon.match(/(45deg|Freeform)$/)
  const iconSuffix = suffixMatch ? suffixMatch[1] : ''
  const iconBase = iconSuffix ? icon.slice(0, -iconSuffix.length) : icon

  let vertical: boolean
  if (orient === 'vertical') vertical = true
  else if (orient === 'horizontal') vertical = false
  else {
    const { w, h } = getShapeDisplaySize(shape)
    vertical = w < h
  }

  if (vertical) {
    // Vertical display: shape.icon points to the Horizontal SVG, so swap
    if (iconBase.endsWith('Horizontal')) {
      icon = iconBase.replace(/Horizontal$/, 'Vertical') + iconSuffix
    }
  } else {
    // Horizontal display: shape.icon points to the Vertical SVG, so swap
    if (iconBase.endsWith('Vertical')) {
      icon = iconBase.replace(/Vertical$/, 'Horizontal') + iconSuffix
    }
  }
  return svgModules[`../../Shapes/SVG/${patternId.value}/${icon}.svg`]
    || svgModules[`../../Shapes/SVG/square/${icon}.svg`]
    || svgModules[`../../Shapes/SVG/45-deg/${icon}.svg`]
    || svgModules[`../../Shapes/SVG/hex/${icon}.svg`]
    || svgModules[`../../Shapes/SVG/hex-90/${icon}.svg`]
    || svgModules[`../../Shapes/SVG/hex-spaced/${icon}.svg`]
    || svgModules[`../../Shapes/SVG/freeform/${icon}.svg`]
    || svgModules[`../../Shapes/SVG/3d/${icon}.svg`]
    || null
}

// Strip the outer <svg> wrapper and return just the inner content scaled to a target size.
// For printed patterns (Aspen, Bamboo, etc.) and any other SVG that has
// filled paths, tag the output with a class so the CSS can preserve the
// original fill colors instead of forcing the gray-outline style.
//
// `orient` overrides the artwork orientation the shape's display size would
// otherwise imply. The rotation card needs it: the card must preview the
// tile the way the CANVAS draws it, and the canvas draws a rect of the
// shape's own w/h turned by the landing rotation. The strip's display size
// is a different question - it is the strip's layout, not the tile's
// footprint - so the two can disagree about which way a slat points.
const getShapeInnerSvg = (shape: Shape, targetW: number, targetH: number, orient?: 'vertical' | 'horizontal'): string | null => {
  const raw = getShapeComponent(shape, orient)
  if (!raw) return null
  // Extract viewBox
  const viewBoxMatch = raw.match(/viewBox=["']([^"']+)["']/)
  if (!viewBoxMatch) return null
  const [vbX, vbY, vbW, vbH] = viewBoxMatch[1].split(/\s+/).map(Number)
  // Extract inner content (everything between <svg> and </svg>)
  const innerMatch = raw.match(/<svg[^>]*>([\s\S]*?)<\/svg>/)
  if (!innerMatch) return null
  const inner = innerMatch[1]
  // Extract the root <svg> tag's fill attribute (if any) so the inner paths
  // can inherit it correctly when the root fill is "none" or a custom color.
  const rootFillMatch = raw.match(/<svg[^>]*\bfill\s*=\s*["']([^"']+)["']/i)
  const rootFill = rootFillMatch ? rootFillMatch[1] : ''
  // Detect filled/stroked printed patterns: look for path/rect elements with
  // a non-"none" fill OR a non-default stroke color. This includes both
  // solid-fill patterns (Aspen, Bamboo) and stroke-based patterns (Geo) and
  // mask-based patterns (Pinstripe, Blossom, Strand). Filled SVGs don't get
  // the default outline style applied so their original colors/strokes are
  // preserved.
  const hasFilledPath = /<path[^>]*\bfill\s*=\s*["'](?!none["'])[^"']+["']/i.test(raw)
    || /<rect[^>]*\bfill\s*=\s*["'](?!none["'])[^"']+["']/i.test(raw)
  const hasStrokedPath = /<path[^>]*\bstroke\s*=\s*["']#/.test(raw)
  const isFilled = hasFilledPath || hasStrokedPath
  const className = isFilled ? 'shape-svg shape-svg--filled' : 'shape-svg'
  if (isFilled) {
    // Don't add fill:none/stroke to the inline style — the SVG's own
    // fill attributes need to work (especially for mask-based patterns
    // like Pinstripe, Blossom, Strand). Preserve the root fill attribute
    // so paths that rely on inheriting fill="none" (like the peak shapes)
    // still render correctly.
    const fillAttr = rootFill ? ` fill="${rootFill}"` : ''
    return `<svg class="${className}"${fillAttr} xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" width="${targetW}" height="${targetH}" style="display:block;width:${targetW}px;height:${targetH}px;vector-effect:non-scaling-stroke">${inner}</svg>`
  }
  return `<svg class="${className}" xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" width="${targetW}" height="${targetH}" style="display:block;width:${targetW}px;height:${targetH}px;fill:none;stroke:#8F8E89;stroke-width:1.33;vector-effect:non-scaling-stroke">${inner}</svg>`
}

// Display size: map each shape to its Figma-accurate display size (per
// Figma nodes 4506:96581 / 4506:96579 for the Mobile - Square Grid and
// 4506:198366 for the Mobile - Shapes (2-Row) component).
const FONT_TILE_DISPLAY_SIZE: Record<string, { w: number; h: number }> = {
  // === Basic squares (Row 1) ===
  LargeSquare: { w: 50, h: 50 },
  Square: { w: 35, h: 35 },
  Quarter: { w: 25, h: 25 },
  Pixel: { w: 16, h: 16 },
  Bit: { w: 12, h: 12 },

  // === Halflaps & Shiplaps (Row 1) — optical sizing for mobile strip so
  // narrow slats remain clearly legible as touchable buttons ===
  LargeHalflap: { w: 25, h: 50 },
  LargeHalflapVertical: { w: 25, h: 50 },
  LargeHalflapHorizontal: { w: 50, h: 25 },
  Halflap: { w: 12.5, h: 25 },
  Shiplap: { w: 16, h: 50 },
  ShiplapVertical: { w: 16, h: 50 },
  ShiplapHorizontal: { w: 50, h: 16 },
  MiniShiplap: { w: 12, h: 36 },
  MiniShiplapVertical: { w: 12, h: 36 },
  MiniShiplapHorizontal: { w: 36, h: 12 },

  // === Triangles (Row 1) — Figma 4506:96579 INSTANCE dims.
  // 90 Triangle: 25×25 single instance (the spec draws two side by side as
  // a pair preview, but the tile-icon is one 25×25 square). The 56x25 pair
  // group width we used previously made the tile look stretched.
  Triangle: { w: 25, h: 25 },
  Small90Triangle: { w: 16, h: 16 },

  // === Engraved 2x2 patterns (Row 1) ===
  Slash: { w: 35, h: 35 },
  Cross: { w: 35, h: 35 },
  FourSquare: { w: 35, h: 35 },
  Parallel: { w: 35, h: 35 },
  Tangent: { w: 50, h: 50 },
  Zen: { w: 50, h: 50 },
  Offset: { w: 50, h: 50 },
  Topo: { w: 50, h: 50 },
  River: { w: 50, h: 50 },
  LargeParallel: { w: 50, h: 50 },
  Converge: { w: 50, h: 50 },
  Subway: { w: 50, h: 50 },

  // === Circles (Row 2) — per Figma 7605:173261 the default display for
  // half-circles in the single-row strip is HORIZONTAL (50×25 for Large,
  // 28×14 for standard, 24×12 for Small) so the curved arc fills the button width
  // instead of rendering as a 9px vertical sliver. ===
  LargeHalfCircle: { w: 50, h: 25 },
  LargeHalfCircleHorizontalFlipped: { w: 50, h: 25 },
  LargeHalfCircleVerticalFlipped: { w: 50, h: 25 },
  LargeHalfCircleHorizontal: { w: 50, h: 25 },
  LargeHalfCircleVertical: { w: 50, h: 25 },
  HalfCircle: { w: 28, h: 14 },
  HalfCircleHorizontalFlipped: { w: 28, h: 14 },
  HalfCircleHorizontal: { w: 28, h: 14 },
  HalfCircleVerticalFlipped: { w: 28, h: 14 },
  HalfCircleVertical: { w: 28, h: 14 },
  SmallHalfCircle: { w: 24, h: 12 },
  SmallHalfCircleVertical: { w: 24, h: 12 },
  SmallHalfCircleHorizontal: { w: 24, h: 12 },
  SmallHalfCircleVerticalFlipped: { w: 24, h: 12 },
  SmallHalfCircleHorizontalFlipped: { w: 24, h: 12 },
  QuarterCircle: { w: 32, h: 32 },
  QuarterCircleBottomLeft: { w: 32, h: 32 },
  QuarterCircleBottomRight: { w: 32, h: 32 },
  QuarterCircleTopLeft: { w: 32, h: 32 },
  QuarterCircleTopRight: { w: 32, h: 32 },

  // === Peaks (Row 2) ===
  SmallPeak: { w: 14, h: 25 },
  LargePeak: { w: 14, h: 50 },
  LargeOffsetPeak: { w: 14, h: 50 },
  LargeCenterPeak: { w: 50, h: 50 },
  LargeUpperPeak: { w: 50, h: 50 },

  // === Printed patterns (Row 2) ===
  Aspen: { w: 50, h: 50 },
  Bamboo: { w: 50, h: 50 },
  Geo: { w: 50, h: 50 },
  Blossom: { w: 50, h: 50 },
  Pinstripe: { w: 50, h: 50 },
  Strand: { w: 50, h: 50 },

  // === Hex / Freeform shapes (per Figma 4506:107421 "Mobile - Hex Grid") ===
  // These were previously sized off a rough 50px-max fallback and sat about
  // 2x too large. The spec INSTANCE sizes are:
  //   Hex 25×21.9, Large Diamond 25×43.8, Small Diamond 12.5×21.9.
  // The diamonds are listed in the spec twice — once wide (40×23 / 20.5×12.5)
  // and once tall (25×43.8 / 12.5×21.9). Our SVGs are portrait (viewBox
  // 120×208 and 60×104, ratio 0.577) and get no rotation in the strip, so the
  // tall forms are the ones that match the art at its natural proportion.
  Hex: { w: 25, h: 21.9 },
  SmallTriangle: { w: 17, h: 13 },
  LargeTriangle: { w: 25, h: 20 },
  SmallDiamond: { w: 12.5, h: 21.9 },
  LargeDiamond: { w: 25, h: 43.8 },

  // === 3D boxes (per Figma 7605:179044 "Square Grid" strip, INSTANCE dims) ===
  // 12"×12" cube 19×19, 12"×36" rect 19×56, 36"×36" cube 56×56. These are
  // the rendered INSTANCE sizes in the spec strip; the earlier 44×57 /
  // 38×104 / 88×107 values came from node 7282:204098, which sizes the
  // desktop swatch sheet rather than this mobile strip.
  // The source art in /new-studio/shapes/3d-active carries the same ratios
  // (32:32, 32:82, 82:82), so these sizes preserve each icon's proportions.
  ThreeDBoxSmall: { w: 19, h: 19 },
  ThreeDBoxMedium: { w: 19, h: 56 },
  ThreeDBoxLarge: { w: 56, h: 56 },

  // === 3D box identifier aliases (lookup keys used in getShapeDisplaySize).
  // The basic 3D shapes use id/prefix/icon values like "Cube36x36x4Set" /
  // "Cube36x36x4" / "CubeLarge4". Map all of those to the right display size
  // so they show at the Figma-correct size instead of the 50px fallback.
  Cube36x36x4Set: { w: 56, h: 56 },
  Cube36x36x2Set: { w: 56, h: 56 },
  Cube36x36x4: { w: 56, h: 56 },
  Cube36x36x2: { w: 56, h: 56 },
  CubeLarge4: { w: 56, h: 56 },
  CubeLarge2: { w: 56, h: 56 },
  Cube12x12x4Set: { w: 19, h: 19 },
  Cube12x12x2Set: { w: 19, h: 19 },
  Cube12x12x4: { w: 19, h: 19 },
  Cube12x12x2: { w: 19, h: 19 },
  CubeSmall4: { w: 19, h: 19 },
  CubeSmall2: { w: 19, h: 19 },
  // Rect12x36 SVGs are 32×82 (ratio ~0.3902). At height 56px, width 22px
  // matches the 32:82 aspect ratio (22 × 82/32 = 56.38px), eliminating
  // letterboxing so the grey background (#D3D2CE) matches the artwork.
  Rect12x36x4Set: { w: 22, h: 56 },
  Rect12x36x2Set: { w: 22, h: 56 },
  Rect12x36x4: { w: 22, h: 56 },
  Rect12x36x2: { w: 22, h: 56 },
  Rect4: { w: 22, h: 56 },
  Rect2: { w: 22, h: 56 },

  // === 3D Engraved variants (per Figma 7282-204096 LARGE 3D BOX SET) ===
  // The engraved variants use the same viewBox proportions as CubeLarge
  // (104×104). Rendered at 106×106 so the LARGE box tiles are consistent
  // with the blank large box footprint on the swatch sheet.
  Cube36x36x4LargeParallel: { w: 56, h: 56 },
  Cube36x36x2LargeParallel: { w: 56, h: 56 },
  Cube36x36x4Tangent: { w: 56, h: 56 },
  Cube36x36x2Tangent: { w: 56, h: 56 },
  Cube36x36x4Zen: { w: 56, h: 56 },
  Cube36x36x2Zen: { w: 56, h: 56 },
  Cube36x36x4River: { w: 56, h: 56 },
  Cube36x36x2River: { w: 56, h: 56 },
  Cube36x36x4Offset: { w: 56, h: 56 },
  Cube36x36x2Offset: { w: 56, h: 56 },
  Cube36x36x4Topo: { w: 56, h: 56 },
  Cube36x36x2Topo: { w: 56, h: 56 },
  Cube36x36x4Converge: { w: 56, h: 56 },
  Cube36x36x2Converge: { w: 56, h: 56 },
  Cube36x36x4Subway: { w: 56, h: 56 },
  Cube36x36x2Subway: { w: 56, h: 56 },

  // === Legacy entries (keep for backwards compat) ===
  HalflapHorizontal: { w: 25, h: 12 },
  HalflapVertical: { w: 12, h: 25 },

  // === Engraved tile sizes (mirror their non-engraved counterparts) ===
  EngravedArc: { w: 50, h: 50 },
  EngravedBackSlash: { w: 35, h: 35 },
  EngravedConverge: { w: 50, h: 50 },
  EngravedCrissCross: { w: 35, h: 35 },
  EngravedCross: { w: 35, h: 35 },
  EngravedFourSquare: { w: 35, h: 35 },
  // 50×50 to match the other 2×2 engraved patterns (Figma 4506:96579).
  EngravedKnot: { w: 50, h: 50 },
  EngravedLargeParallel: { w: 50, h: 50 },
  EngravedOffset: { w: 50, h: 50 },
  EngravedParallel: { w: 35, h: 35 },
  EngravedRiver: { w: 50, h: 50 },
  EngravedSubway: { w: 50, h: 50 },
  EngravedTangent: { w: 50, h: 50 },
  EngravedTopo: { w: 50, h: 50 },
  EngravedZen: { w: 50, h: 50 },
  EngravedSlash: { w: 35, h: 35 },
  EngravedForwardSlash: { w: 35, h: 35 },
}

const getShapeDisplaySize = (shape: Shape) => {
  // Try multiple lookup keys: id, icon, prefix (in that order), each with
  // grid-specific suffixes stripped so the FONT_TILE_DISPLAY_SIZE lookup
  // works across all grids. We check multiple candidates to handle shapes
  // whose icon/prefix/id differ (e.g. SmallHalfCircle has icon
  // "HalfCircleHorizontalFlipped" and prefix "SmallHalfCircle").
  const candidates: string[] = []
  for (const raw of [shape.id, shape.prefix, shape.icon]) {
    if (raw) {
      candidates.push(raw)
      // Strip grid-specific "45deg" suffix (e.g. Square45deg -> Square)
      candidates.push(raw.replace(/45deg$/, ''))
    }
  }
  for (const key of candidates) {
    if (FONT_TILE_DISPLAY_SIZE[key]) return FONT_TILE_DISPLAY_SIZE[key]
  }
  // Fallback: scale shape.w/h to fit within 50px max
  const w = Number(shape.w) || 30
  const h = Number(shape.h) || 30
  const max = 50
  const ratio = w / h
  if (ratio >= 1) {
    return { w: max, h: max / ratio }
  }
  return { w: max * ratio, h: max }
}

// Per-shape tile frame widths from Figma node 7605:173261 (Mobile - Shapes)
// and companion tab components (7605:173262 Engraved, 7605:173260 Printed,
// 7605:173259 Boxes). Setting each button to its Figma frame width keeps the
// spacing between shapes compact and faithful to the design spec (which uses
// 8px gaps between fixed-width frames), rather than letting buttons expand to
// the full label width with oversized empty space.
const FONT_TILE_FRAME_WIDTH: Record<string, number> = {
  // === Basic shapes (Figma 7605:173261) ===
  LargeSquare: 50,
  Square: 36,
  Quarter: 37,
  Pixel: 21,
  Bit: 21,
  LargeHalflap: 37,
  Shiplap: 38,
  Halflap: 36,
  MiniShiplap: 39,
  Small90Triangle: 50,
  '90Triangle': 42,
  Triangle: 42,
  RightTopTriangle: 42,
  LeftBottomTriangle: 42,
  LargeHalfCircle: 53,
  QuarterCircle: 40,
  QuarterCircleBottomRight: 40,
  QuarterCircleBottomLeft: 40,
  SmallHalfCircle: 52,
  HalfCircle: 27,
  HalfCircleHorizontalFlipped: 27,
  LargeOffsetPeak: 31,
  LargePeak: 30,
  SmallPeak: 28,
  LargeCenterPeak: 30,
  LargeUpperPeak: 31,

  // === Other 2D shapes (Figma 7605:173261 "Other shapes") ===
  LargeDiamond: 53,
  SmallDiamond: 53,
  Hex: 53,
  LargeTriangle: 53,
  SmallTriangle: 53,

  // === Engraved 1×1 tiles (Figma 7605:173262) ===
  EngravedSlash: 35,
  EngravedForwardSlash: 35,
  EngravedBackSlash: 35,
  EngravedCross: 35,
  EngravedFourSquare: 35,
  EngravedParallel: 35,
  EngravedCrissCross: 35,
  Slash: 35,
  Cross: 35,
  FourSquare: 35,
  Parallel: 35,

  // === Engraved 2×2 tiles (Figma 7605:173262) ===
  EngravedTangent: 53,
  EngravedZen: 53,
  EngravedRiver: 53,
  EngravedOffset: 53,
  EngravedLargeParallel: 53,
  EngravedTopo: 53,
  EngravedConverge: 53,
  EngravedSubway: 53,
  EngravedArc: 53,
  EngravedKnot: 53,
  Tangent: 53,
  Zen: 53,
  River: 53,
  Offset: 53,
  LargeParallel: 53,
  Topo: 53,
  Converge: 53,
  Subway: 53,

  // === Printed patterns (Figma 7605:173260) ===
  Aspen: 50,
  Bamboo: 50,
  Geo: 50,
  Pinstripe: 50,
  Blossom: 50,
  Strand: 50,

  // === 3D Boxes (Figma 7605:173259) ===
  Cube12x12x4Set: 50,
  Cube12x12x2Set: 50,
  Cube12x12x4: 50,
  Cube12x12x2: 50,
  CubeSmall4: 50,
  CubeSmall2: 50,
  Rect12x36x4Set: 50,
  Rect12x36x2Set: 50,
  Rect12x36x4: 50,
  Rect12x36x2: 50,
  Rect4: 50,
  Rect2: 50,
  Cube36x36x4Set: 68,
  Cube36x36x2Set: 68,
  Cube36x36x4: 68,
  Cube36x36x2: 68,
  CubeLarge4: 68,
  CubeLarge2: 68,
  Cube36x36x4LargeParallel: 66,
  Cube36x36x2LargeParallel: 66,
  Cube36x36x4Tangent: 68,
  Cube36x36x2Tangent: 68,
  Cube36x36x4Zen: 68,
  Cube36x36x2Zen: 68,
  Cube36x36x4River: 66,
  Cube36x36x2River: 66,
  Cube36x36x4Offset: 68,
  Cube36x36x2Offset: 68,
  Cube36x36x4Topo: 66,
  Cube36x36x2Topo: 66,
  Cube36x36x4Converge: 66,
  Cube36x36x2Converge: 66,
  Cube36x36x4Subway: 68,
  Cube36x36x2Subway: 68,
}

const getTileFrameWidth = (shape: Shape): number | undefined => {
  if (patternId.value === '45-deg' && !is3DBoxShape(shape)) {
    const size = getShapeDisplaySize(shape)
    return Math.ceil((size.w + size.h) / SQRT_2)
  }
  const candidates: string[] = []
  for (const raw of [shape.id, shape.prefix, shape.icon]) {
    if (raw) {
      candidates.push(raw)
      candidates.push(raw.replace(/45deg$/, ''))
      candidates.push(raw.replace(/(Vertical|Horizontal|HorizontalFlipped|VerticalFlipped)$/, ''))
    }
  }
  for (const key of candidates) {
    if (FONT_TILE_FRAME_WIDTH[key]) return FONT_TILE_FRAME_WIDTH[key]
  }
  return undefined
}

const getTileBtnStyle = (shape: Shape) => {
  const width = getTileFrameWidth(shape)
  return width ? { width: `${width}px` } : {}
}

const isShapeActive = (shape: Shape) => {
  const selected = gridStore.selectedShape
  if (!selected) return false
  if (selected.id === shape.id) return true
  return shape.children?.some(c => c.id === selected.id) ?? false
}

// Swaps the selected tile between its 9mm and 18mm variants. This only
// affects the active tile — the strip keeps listing the 9mm set, so tapping
// Back returns to the default shapes.
const setDepth = (depth: 9 | 18) => {
  const current = gridStore.selectedShape
  if (!current) return
  const cleanPrefix = current.prefix?.replace('Layered', '')
  if (depth === 18 && !isLayeredShape(current)) {
    const layered = LAYERED_ENGRAVED_SHAPES.find(s => s.prefix === 'Layered' + cleanPrefix) || LAYERED_ENGRAVED_SHAPES[0]
    if (layered) {
      const match = layered.children?.find(c => c.rotation === currentRotation.value) || layered
      picker.selectShape(match, new MouseEvent('click'))
    }
  } else if (depth === 9 && isLayeredShape(current)) {
    const standard = ENGRAVED_2X2_SQUARE_SHAPES.find(s => s.prefix === cleanPrefix) || ENGRAVED_2X2_SQUARE_SHAPES[0]
    if (standard) {
      const match = standard.children?.find(c => c.rotation === currentRotation.value) || standard
      picker.selectShape(match, new MouseEvent('click'))
    }
  }
}

const selectShape = (shape: Shape, event: MouseEvent) => {
  // Picking a shape from the strip means "I want to place THIS next", so
  // anything already selected on the canvas is released first.
  //
  // Without this, a tile that was selected on the grid (tapped to inspect
  // it, or to select it for delete/copy) stayed selected, and the ADD tool
  // then placed the newly-picked shape ON TOP of it - which the studio
  // rendered as the selected tile having been changed into the new shape.
  // The tile the user was looking at silently became a different shape.
  //
  // The selection is released BEFORE the shape is picked, because picking the
  // shape also pushes it into the picker as the current selection.
  if (gridStore.selectedItemsOnGrid.length > 0) {
    gridStore.unselectItemsOnGrid()
  }

  picker.selectShape(shape, event)
  // If this shape supports rotation, swap the strip for the rotation card
  // (Figma 7675:162616). The user picks a rotation, then we drop back to the
  // strip so they can place the tile.
  const supportsRotation = !isRotationLockedShape(shape)
  pickerMode.value = supportsRotation ? 'rotation' : 'strip'
}

const isHexShape = (shape: Shape | null | undefined): boolean => {
  if (!shape) return false
  return shape.type === 'hex' || (shape.prefix || '').startsWith('Hex') || (shape.id || '').startsWith('Hex')
}

// Mirror of shouldShowRotationControl but for an unsaved (just-picked) shape.
// On hex-family grids, Hex has no rotation/orientation children (matching desktop,
// which has no children dropdown or rotation controls for Hex). Tapping it selects
// the tile directly on the strip without opening the rotation card.
const isRotationLockedShape = (shape: Shape | null | undefined): boolean => {
  if (!shape) return true
  if (isHexGrid.value && isHexShape(shape)) return true
  return Boolean((shape as any).rotationLocked
    || (shape as any).metadata?.rotation_locked
    || (shape as any).attrs?.metadata?.rotation_locked)
}

const GRID_TURN_45DEG = 45

const norm = (r: number) => ((r % 360) + 360) % 360

// The orientations a shape is authored with: its own rotation, then each
// child's authored rotation. This is what the desktop picker lists, and each
// entry is the rotation the tile lands at.
const authoredOrientations = (shape: Shape | null | undefined): number[] => {
  if (!shape) return [0]
  const parentRotation = shape.rotation ?? 0
  const childRotations = (shape.children || [])
    .map(child => child.rotation ?? 0)
    .filter(rotation => norm(rotation) !== norm(parentRotation))
  return [parentRotation, ...childRotations]
}

// The shape FAMILY the current selection belongs to: the parent that lists the
// selected shape among its children.
//
// Selecting a rotation option can change the selected shape (the halflaps and
// shiplaps list a child that is a different shape), and the card's options
// must keep describing the whole family. Resolving against the selected child
// instead made every option resolve to that child, so picking the first
// variation again still showed the second one.
const selectedShapeFamily = computed<Shape | null>(() => {
  const shape = gridStore.selectedShape
  if (!shape) return null
  if (shape.children?.length) return shape
  const list = (GRID_SHAPES as any)[patternId.value] || []
  const parent = list.find((candidate: Shape) =>
    candidate.children?.some((child: Shape) => child.id === shape.id))
  return parent || shape
})

// Rotation control logic
const currentRotation = computed(() => {
  const item = gridStore.selectedItemsOnGrid[0]
  if (item && item.rotation !== undefined) {
    const rot = Number(item.rotation)
    if (is45degGrid.value && rot % 45 === 0 && rot % 90 !== 0) {
      return ((rot - GRID_TURN_45DEG) % 360 + 360) % 360
    }
    return rot
  }
  const selected = gridStore.selectedRotation
  if (selected != null) {
    return norm(selected)
  }
  const baseRot = Number(selectedShapeFamily.value?.rotation || 0)
  return is45degGrid.value ? norm(baseRot - GRID_TURN_45DEG) : norm(baseRot)
})

// The rotation the card's HEADER preview draws the shape at.
//
// The header shows the shape the user currently has selected, turned to the
// rotation that tile lands at. On the 45° grid that is the shape's own
// rotation plus the grid's 45° turn - the same numbers the rotation options
// use - so the header agrees with the option that is selected.
//
// It must NOT go through previewRotation: that takes a USER angle (0/90/
// 180/270) and adds the grid's turn, so passing the shape's rotation to it
// would add the 45° twice and show the header at 90° when the shape lands
// at 45°.
const headerPreviewRotation = computed(() => {
  const shape = gridStore.selectedShape
  if (!shape) return 0
  const flip = engravedBaseArtFlip(shape.prefix)
  if (patternId.value !== '45-deg') {
    const landing = Number(gridStore.selectedRotation ?? shape.rotation ?? 0)
    return landing + flip
  }
  // On the 45° grid, tiles are placed with a 45° baseline. If a specific
  // rotation is selected (0, 90, 180, 270), add the 45° baseline.
  // If no rotation is selected yet, use the shape's own rotation — if the
  // shape already carries the 45° turn (like EngravedTangent45deg, rotation: 45),
  // use it as-is; if it's from the square catalog (like Pinstripe, rotation: 0),
  // add the 45° turn so the header preview matches the 45° orientation.
  if (gridStore.selectedRotation != null) {
    return (GRID_TURN_45DEG + Number(gridStore.selectedRotation)) % 360 + flip
  }
  const baseRotation = Number(shape.rotation ?? 0)
  const turn = (baseRotation % 90 === 0) ? GRID_TURN_45DEG : 0
  return (baseRotation + turn) % 360 + flip
})

const isHexGrid = computed(() => patternId.value.includes('hex'))
const is45degGrid = computed(() => patternId.value === '45-deg')

// The engraved 2×2 families, by prefix. The 45° grid lists its own
// engraved entries (EngravedTangent45deg and friends) in the grid's shape
// list, so the Engraved tab can pick them out of it by prefix.
const ENGRAVED_2X2_PREFIXES = new Set([
  'Converge', 'Knot', 'LargeParallel', 'Offset', 'River', 'Subway', 'Tangent', 'Topo', 'Zen',
])

// Shapes that don't have meaningful rotated variants (per Figma 7675:169236).
// For these we only expose the default rotation in the mobile rotation
// picker, so the user still sees the card but with a single option. The
// studio's rotation feature itself is untouched - this only limits how many
// options the card offers.
const NON_ROTATABLE_SHAPE_IDS = new Set<string>([
  'LargeSquare',
  'Square',
  'Quarter',
  'Pixel',
  'Bit',
  'Cross',
  'EngravedCross',
  '4Square',
  'FourSquare',
  'EngravedFourSquare',
  'Knot',
  'EngravedKnot',
])
const isNonRotatableShape = (shape: Shape | null | undefined): boolean => {
  if (!shape) return false
  const id = shape.id || ''
  const prefix = shape.prefix || ''
  return NON_ROTATABLE_SHAPE_IDS.has(id)
    || NON_ROTATABLE_SHAPE_IDS.has(prefix)
    || NON_ROTATABLE_SHAPE_IDS.has(prefix.replace('Layered', ''))
}

// Shapes with 180° rotational symmetry that only need 2 rotation options (0° and 90°):
// - Diamonds (rhombus: 0° is vertical, 90° is horizontal; 180° and 270° are duplicates)
// - Plain rectangles (halflaps and shiplaps: 0° vertical, 90° horizontal; 180° and 270° are duplicates)
// - 2-fold symmetric engraved tiles (Parallel lines, Slash diagonal, River)
const TWO_ROTATION_90_SHAPE_IDS = new Set<string>([
  'LargeDiamond',
  'SmallDiamond',
  'Diamond',
  'LargeDiamondHorizontal',
  'SmallDiamondHorizontal',
  'SmallDiamondSpaced',
  'SmallDiamond60Spaced',
  'SmallDiamond120Spaced',
  'LargeHalflap',
  'LargeHalflapVertical',
  'LargeHalflapHorizontal',
  'Halflap',
  'HalflapVertical',
  'HalflapHorizontal',
  'Shiplap',
  'ShiplapVertical',
  'ShiplapHorizontal',
  'MiniShiplap',
  'MiniShiplapVertical',
  'MiniShiplapHorizontal',
  'Parallel',
  'LargeParallel',
  'Slash',
  'River',
])

const isTwoRotation90Shape = (shape: Shape | null | undefined): boolean => {
  if (!shape) return false
  if (shape.type === 'diamond') return true
  const id = shape.id || ''
  const prefix = (shape.prefix || '').replace('Layered', '')
  return TWO_ROTATION_90_SHAPE_IDS.has(id)
    || TWO_ROTATION_90_SHAPE_IDS.has(prefix)
    || TWO_ROTATION_90_SHAPE_IDS.has(id.replace(/^(Layered|Engraved)/, ''))
}

// On hex-family grids, equilateral triangles only fit the lattice pointing UP (0°)
// and pointing DOWN (180°). Showing 90°/270° or 6 60° slices introduces duplicates
// or invalid angles, so only 2 options (0° and 180°) are needed.
const isTwoRotation180Shape = (shape: Shape | null | undefined): boolean => {
  if (!shape) return false
  if (isHexGrid.value && (shape.type === 'triangle' || (shape.prefix || '').includes('Triangle'))) {
    return true
  }
  return false
}

// The rotation options a shape offers.
//
// Desktop's picker lists a shape's orientations as its OWN rotation followed by
// each child's authored rotation (components/NewStudio/ShapePicker.vue renders
// the parent then shape.children, and each child carries the rotation its tile
// lands at). The mobile card offers the same set of orientations.
//
// The labels are absolute rotations: the shape's own rotation and its
// children's, which are the rotations the tiles land at. On the 45° grid the
// store adds the grid's own 45° turn to the chosen angle, so the labels there
// are the authored orientation less that turn.
//
// A shape whose children all carry the parent's rotation (the halflaps, whose
// child is a different SHAPE rather than a different orientation) has no
// rotation variants of its own, and falls back to the four cardinal angles.
const rotationAngles = computed(() => {
  const shape = selectedShapeFamily.value
  if (!shape) return [0]
  if (isNonRotatableShape(shape)) return [0]
  const authored = authoredOrientations(shape)
  if (authored.length > 1) {
    // The labels are the authored orientations, in the shape's own order
    // (the parent first, then its children - the same order the desktop
    // picker lists them). On the 45° grid the label is the authored
    // orientation less the grid's 45° turn, which the store adds back when
    // it places the tile.
    //
    // The order must NOT be sorted: the label names an orientation, and
    // sorting the labels decouples them from the orientation each one
    // names. Shiplap on the 45° grid is authored [135, 45] (parent, child),
    // so its labels are [90, 0]; sorting them to [0, 90] made the 0° option
    // resolve to the child, and picking the first orientation gave the
    // second one.
    return is45degGrid.value
      ? authored.map(rotation => norm(rotation - GRID_TURN_45DEG))
      : authored
  }
  if (isTwoRotation180Shape(shape)) return [0, 180]
  if (isTwoRotation90Shape(shape)) return [0, 90]
  return [0, 90, 180, 270]
})

// The rotation card's preview.
//
// The card draws the shape at a fixed 44x44 inside a 60x60 tile. On the
// grids where the shape is turned a quarter turn or more, a fixed square
// box does not survive the turn: the painted artwork's bounding box grows
// with it. On the 45° grid a 44x44 box turned 45° paints a 62.2px square,
// which spills out of the tile's 56px inner area and reads as a shape
// hanging over the border.
//
// So the box is sized so the TURNED artwork lands inside the tile: a w x h
// artwork turned by `deg` paints a box of (w x sin + h x cos) in each
// direction, and the box is scaled down to fit. The artwork keeps its own
// aspect, so the turn is applied about the artwork's centre and the result
// is centred in the tile.
const ROTATION_TILE_SIZE = 60
const ROTATION_TILE_BORDER = 2
const ROTATION_PREVIEW_SIZE = 44

// Relative scale for square family shapes so the user can easily identify
// that Square, Quarter, Pixel, and Bit are smaller than Large Square inside
// the rotation tab and header preview.
const SQUARE_ROTATION_PREVIEW_SIZES: Record<string, { card: number; header: number }> = {
  LargeSquare: { card: 44, header: 36 },
  Square:      { card: 32, header: 26 },
  Quarter:     { card: 22, header: 18 },
  Pixel:       { card: 16, header: 13 },
  Bit:         { card: 12, header: 10 },
}

const getRotationCardBaseSize = (shape: Shape | null | undefined): { card: number; header: number } => {
  if (!shape) return { card: ROTATION_PREVIEW_SIZE, header: 36 }
  for (const raw of [shape.id, shape.prefix, shape.icon]) {
    if (!raw) continue
    const key = raw.replace(/(45deg|Freeform)$/, '')
    if (SQUARE_ROTATION_PREVIEW_SIZES[key]) {
      return SQUARE_ROTATION_PREVIEW_SIZES[key]
    }
  }
  return { card: ROTATION_PREVIEW_SIZE, header: 36 }
}

const headerPreviewBox = computed(() => {
  const size = getRotationCardBaseSize(gridStore.selectedShape).header
  return { w: size, h: size }
})

// The space the preview has to land in inside the tile.
const rotationTileInner = ROTATION_TILE_SIZE - ROTATION_TILE_BORDER * 2

// How much the artwork's painted box grows when it is turned by `deg`.
const rotationGrowth = (w: number, h: number, deg: number): number => {
  const radians = (deg % 360) * Math.PI / 180
  return w * Math.abs(Math.sin(radians)) + h * Math.abs(Math.cos(radians))
}

// The size the preview's SVG element is drawn at.
//
// It scales from each shape's base preview size (44px for Large Square down to
// 12px for Bit) and scales down further if the turn grows the artwork beyond
// the tile's inner area.
const rotationPreviewBox = (deg: number): { w: number; h: number } => {
  const variant = previewVariantForAngle(deg)
  const baseSize = getRotationCardBaseSize(variant.shape).card
  if (!variant.shape) {
    return { w: baseSize, h: baseSize }
  }
  const growth = rotationGrowth(
    baseSize,
    baseSize,
    variant.rotation,
  )
  if (growth <= rotationTileInner) {
    return { w: baseSize, h: baseSize }
  }
  const scale = rotationTileInner / growth
  return { w: baseSize * scale, h: baseSize * scale }
}

// The transform for the preview's wrapper: the turn itself, plus the box
// that keeps the grown artwork inside the tile.
const rotationPreviewStyle = (deg: number) => {
  const variant = previewVariantForAngle(deg)
  const box = rotationPreviewBox(deg)
  return {
    transform: `rotate(${variant.rotation}deg)`,
    transformOrigin: 'center',
    width: `${box.w}px`,
    height: `${box.h}px`,
  }
}

// Which way the card's artwork must point so the preview matches the tile
// the canvas places.
//
// The canvas draws a rect of the shape's OWN w/h and turns it by the
// rotation the tile lands at. The preview turns its artwork by that same
// rotation, so the artwork's own long axis has to be the shape's own long
// axis too. Otherwise the two disagree by 90 degrees.
//
// The 45° grid's Shiplap is the case that bit: the shape's own rect is
// 240x60 (landscape), but the strip's DISPLAY size for it is vertical, so
// the preview loaded the portrait art for one option and the landscape art
// for the other. The portrait art turned 135° and the landscape art turned
// 45° both put the long axis at 45°, so the two options drew the same
// diagonal even though the canvas placed them 90° apart.
const ownArtOrient = (shape: Shape | null | undefined): 'vertical' | 'horizontal' => {
  if (!shape) return 'horizontal'
  const w = Number(shape.w) || 0
  const h = Number(shape.h) || 0
  if (!w || !h) return 'horizontal'
  return h > w ? 'vertical' : 'horizontal'
}

const rotationPreviewOrient = (deg: number): 'vertical' | 'horizontal' =>
  ownArtOrient(previewVariantForAngle(deg).shape)

// The Depth row (Figma 7372:211744) applies to the 2×2 engraved set, which
// is the only family offered at both 9mm and 18mm. 1×1 tiles are 9mm-only,
// so they show Rotation alone. A layered shape (already 18mm) counts too, so
// the user can switch back down to the 9mm standard variant.
const hasDepthOptions = computed(() => {
  const shape = gridStore.selectedShape
  if (!shape) return false
  const prefix = shape.prefix || ''
  if (prefix.startsWith('Layered')) return true
  return ENGRAVED_2X2_SQUARE_SHAPES.some(s => s.prefix === prefix)
})

// Depth for the card's toggle is read from the selected shape itself —
// a Layered* prefix is 18mm, anything else 9mm. The strip stays on the 9mm
// list independently, so going Back to the default tab shows 9mm shapes
// regardless of which depth was last picked for the active tile.
const selectedDepth = computed<9 | 18>(() =>
  (gridStore.selectedShape?.prefix || '').startsWith('Layered') ? 18 : 9
)

// Rotation-card header (Figma 7372:211744). The spec shows just the pattern
// name ("Zen") with a size line ("2×2 ft engraved tile") — the depth lives in
// its own row, so it is stripped from both here to avoid saying it twice.
const selectedShapeBaseName = computed(() => {
  const label = gridStore.selectedShape?.label || ''
  return label.replace(/\s*[·(].*$/, '').trim() || label
})

const selectedShapeSubtitle = computed(() => {
  const prefix = gridStore.selectedShape?.prefix || ''
  const is1x1 = ENGRAVED_1X1_SQUARE_SHAPES.some(s => s.prefix === prefix)
  const is2x2 = prefix.startsWith('Layered') || ENGRAVED_2X2_SQUARE_SHAPES.some(s => s.prefix === prefix)
  if (is1x1) return "1×1 ft engraved tile"
  if (is2x2) return "2×2 ft engraved tile"
  return 'Felt shape'
})

// The half circles are stored as a HORIZONTAL rectangle SVG (a 2:1
// viewBox) and turned upright with a CSS rotate(90deg) - the repo has no
// vertical artwork for them.
//
// The tile box, though, is the VERTICAL size from the Figma spec
// (LargeHalfCircle 25x50, HalfCircle 12.5x25, SmallHalfCircle 9x17). So
// the SVG was being asked to draw a 2:1 picture into a 1:2 box, and with
// the default preserveAspectRatio="xMidYMid meet" the artwork was
// letterboxed to HALF its height and centred - which is why the half
// circle read as a small D floating in the middle of its slot instead of
// filling it, and sat lower than the shapes beside it.
//
// The fix is to give the SVG a box with the artwork's OWN aspect ratio
// (50x25 for the large one) and let the rotate(90deg) turn that into the
// vertical 25x50. Nothing is letterboxed, so the artwork fills its box and
// lands on the same baseline as every other tile.
//
// Figma 4506:96581 specifies the Large Half Circle as a 25x50 INSTANCE,
// i.e. the full vertical footprint - which is what this now draws.
const HALF_CIRCLE_PREFIXES = ['LargeHalfCircle', 'HalfCircle', 'SmallHalfCircle']

const isHalfCircle = (shape: Shape) => HALF_CIRCLE_PREFIXES.includes(shape.prefix || '')

// The size the SVG element should be given, before any CSS rotation.
//
// Normally that is the display size. For the half circles it is the same
// footprint with the axes swapped, so the artwork's 2:1 viewBox matches the
// box and nothing is letterboxed.
//
const getTileIconBoxSize = (shape: Shape) => {
  const size = getShapeDisplaySize(shape)
  // Half circles in this repo always have horizontal 2:1 SVG artwork (e.g.
  // LargeHalfCircle 240x120, HalfCircle 120x60). When the display size is
  // vertical (size.w < size.h, such as 25x50), swap axes so the SVG is
  // rendered at the artwork's own 2:1 aspect ratio. This prevents letterboxing
  // across all grids (including the 45° grid, where letterboxing made the
  // half-circle float 13px above the baseline of adjacent tiles).
  if (isHalfCircle(shape) && size.w < size.h) {
    return { w: size.h, h: size.w }
  }
  // A 90°-turned hex: the 104x120 SVG is portrait while the display size
  // (25x21.9) is landscape. Lay the SVG out at the swapped box so the
  // artwork's own aspect fills it, then the turn lands it on the footprint.
  if (isTurnedTileIcon(shape)) {
    return { w: size.h, h: size.w }
  }
  return size
}

// The footprint the tile RESERVES in the strip.
//
// This is the display size - the vertical one from the Figma spec for the
// half circles - even though the SVG element inside is laid out at the
// artwork's own (horizontal) aspect ratio and is turned upright with a CSS
// rotation.
//
// A CSS transform does not change layout, so the rotated artwork would
// otherwise spill out of the space reserved for it and hang below the
// baseline the other tiles sit on. Giving the icon container the rotated
// footprint makes the two agree: the container reserves 25x50, the SVG
// inside is 50x25 centred within it, and the rotation about that shared
// centre lands the artwork exactly on the 25x50 footprint.
//
// On the 45° grid the artwork is turned 45°, which grows its painted
// bounding box: a w x h rectangle turned 45° has an AABB of (w + h) / sqrt(2)
// in BOTH directions - the corners swing out past the box. The container has
// to reserve THAT much, or the artwork grows upward into the tile's label.
//
// Figma makes the same allowance. Its 45° variant gives the Large Square a
// 70.7 x 70.7 `rotations` frame (50 x 50 turned 45°), the Square 50 x 50
// (35 x 35 turned 45°), and the Pixel 23 x 23 (16 x 16 turned 45°) - each
// exactly the un-rotated size x sqrt(2) for a square.
//
// The 45° grid is the only one that needs this: the other grids draw the
// artwork un-rotated (or turned a full 90°, which does not change the
// bounding box).
const SQRT_2 = Math.SQRT2

// 3D boxes are isometric drawings of 3D blocks, not 2D tiles on a 45° grid.
// They live only in the Boxes tab and are shown in their natural orientation
// on every grid (matching desktop's ThreeDBoxesBlank.vue), so they are exempt
// from the 45° rotation / diamond footprint the 2D tiles get.
const is3DBoxShape = (shape: Shape) => activeTab.value === 'boxes' || !!get3DShapeSrc(shape)

const getTileIconFootprintStyle = (shape: Shape) => {
  const size = getShapeDisplaySize(shape)
  if (patternId.value === '45-deg' && !is3DBoxShape(shape)) {
    // The SVG element keeps its own w x h (the artwork's real size) and is
    // centred in this box; rotating it about that shared centre makes the
    // painted square exactly fill the box.
    const side = Math.ceil((size.w + size.h) / SQRT_2)
    return { width: `${side}px`, height: `${side}px` }
  }
  return { width: `${size.w}px`, height: `${size.h}px` }
}

// True for hex tiles on grids where the shape is turned 90° (hex, hex-spaced,
// freeform). The artwork in Hex.svg is pointy-topped (viewBox 104x120);
// turning it 90° makes it flat-topped, matching the canvas grid and the desktop.
const isTurnedTileIcon = (shape: Shape) => {
  if (patternId.value === '45-deg') return false
  return isHexShape(shape) && ((shape.rotation ?? 0) % 180 === 90)
}

// True for shapes (half circles and 90°-turned hex) that are drawn at the
// opposite aspect ratio and turned upright with a CSS rotation. Those tiles
// need the SVG free of the max-width clamp, because the SVG is wider than
// the footprint it is rotated into.
const isRotatedHalfCircle = (shape: Shape) => {
  if (patternId.value === '45-deg') return false
  const size = getShapeDisplaySize(shape)
  if (isHalfCircle(shape) && size.w < size.h) return true
  if (isTurnedTileIcon(shape)) return true
  return false
}

// Display the shape in its natural orientation. For the 45° grid, apply a
// 45° rotation so the shape preview matches the grid pattern. The shape's
// intrinsic rotation only applies when placing the tile on the grid.
const getTileIconStyle = (shape: Shape) => {
  if (patternId.value === '45-deg') {
    // 3D boxes are never turned 45°: they are isometric drawings, not
    // diamond-oriented 2D tiles. Rotating them here (while the grey ground
    // stayed square) is what left the unavailable boxes looking broken.
    if (is3DBoxShape(shape)) return {}
    return { transform: 'rotate(45deg)', transformOrigin: 'center' }
  }
  // Hex tiles: the Hex.svg asset is authored pointy-topped (viewBox 104x120).
  // On hex and hex-spaced grids, the shape definition specifies rotation: 90
  // (flat-topped, matching the canvas grid and desktop picker). Apply the
  // authored rotation so the strip icon matches the placed tile.
  if (isTurnedTileIcon(shape)) {
    return { transform: `rotate(${shape.rotation}deg)`, transformOrigin: 'center' }
  }
  // Half circles (Large, standard, Small) only have a horizontal-rect SVG
  // file in the repo. When the shape is meant to be displayed vertically
  // (taller than wide) per the Figma spec, rotate the SVG 90° so the
  // half-circle path appears vertical.
  const size = getShapeDisplaySize(shape)
  if (size.w < size.h) {
    if (isHalfCircle(shape)) {
      return { transform: 'rotate(90deg)', transformOrigin: 'center' }
    }
  }
  return {}
}

// The shape variant the rotation card should draw for a given angle, and
// the rotation it lands at.
//
// The card's option labels ARE the shape's authored orientations: its own
// rotation first, then each child's authored rotation. Each option must draw
// the shape the way that orientation looks, which is what the tile lands as
// on the grid.
//
// The engraved families list their variants as CHILDREN: the parent is the
// base shape and each child is one rotation of it, carrying its own rotation
// and its own artwork. On the 45° grid the parent's rotation is 45 and the
// children's are 135 / 225 / 315, and their artwork is the 45°-turned set
// (EngravedTangent45deg and friends).
//
// So the card must draw the CHILD that matches the option, and rotate it by
// the child's own rotation - the same numbers the tile lands at on the
// grid. Drawing the parent and turning it by the user's angle is what put
// the 90/180/270 options in the default orientation.
const previewVariantForAngle = (deg: number): { shape: Shape; rotation: number } => {
  const shape = selectedShapeFamily.value
  if (!shape) return { shape: gridStore.selectedShape!, rotation: 0 }
  const children = shape.children
  if (!children?.length) {
    return { shape, rotation: previewRotation(deg) }
  }
  const parentRotation = shape.rotation ?? 0

  // Children come in two flavours and they must not be confused:
  //
  //   ROTATION variants - the engraved families (Tangent, Zen, ...) list one
  //   child per quarter turn, each carrying the rotation its tile LANDS at
  //   (45° grid: 135 / 225 / 315). Those are the ones the card should draw.
  //
  //   SHAPE variants - the halflaps and shiplaps list a child that is a
  //   DIFFERENT SHAPE (Large Halflap Horizontal), and it carries the SAME
  //   rotation as its parent (45 on the 45° grid, none on the square grid).
  //   That child is not a rotation, so drawing it would put the options in
  //   the wrong orientation.
  //
  // So a child is a rotation variant only when its own rotation differs from
  // the parent's. Anything else falls through to rotating the parent.
  //
  // The child is matched on its OWN authored rotation, which is the rotation
  // its tile lands at. Matching on `parentRotation + deg` instead shifted every
  // option by the parent's turn: the Quarter Circle (parent rotation 90) drew
  // its 90° option from the 180° child, so that option showed the wrong
  // orientation - and because a quarter disc paints a wider diagonal when
  // turned, it also burst out of its option square while its siblings did not.
  //
  // On the 45° grid the option labels are the authored orientation less the
  // grid's own 45° turn (the store adds it back when it places the tile), so
  // the child is matched on the label plus that turn.
  const gridTurn = is45degGrid.value ? GRID_TURN_45DEG : 0
  const target = norm(deg + gridTurn)
  const child = children.find(c => norm(c.rotation ?? 0) === target && norm(c.rotation ?? 0) !== norm(parentRotation))

  if (child) {
    // The child carries the rotation of the tile it lands at. On top of that
    // sits the Zen/Topo base-art correction, which is about the artwork's own
    // orientation rather than the tile's.
    return {
      shape: child,
      rotation: norm((child.rotation ?? 0) + engravedBaseArtFlip(child.prefix)),
    }
  }
  // No rotation variant for this option: draw the PARENT at the orientation
  // this option names. The labels are absolute rotations (the shape's own
  // rotation and its children's), so the parent is drawn at that angle rather
  // than at the bare user angle.
  return { shape, rotation: norm(target + engravedBaseArtFlip(shape.prefix)) }
}

// The preview must show the tile the way it will land on the grid. The grid
// draws the tile from its catalog photo; the preview draws the line-art SVG.
// The Zen and Topo artwork is drawn turned in the SVG relative to the photo,
// so those two carry a base-art turn on top of the orientation the option
// names (see composables/shapes/engravedOrientation.ts). Every other
// pattern's artwork already agrees with the tile and is left alone.
//
// On the 45° grid the tile lands turned 45°, and the store adds that turn to
// the angle the user picks (stores/grid.ts). The option labels there are the
// authored orientation less the turn, so the preview adds it back:
//
//   0°   -> 45°    (the grid's baseline)
//   90°  -> 135°
//   180° -> 225°
//   270° -> 315°
//
// This matches the spec: Figma's 45° rotation picker (7621:161862) uses the
// 45°-turned artwork - "Frame 82", a 38x38 diamond - for ALL FOUR options,
// and turns the whole tile by the user's 0/90/180/270 on top. The square-grid
// picker (7621:159132) uses the un-turned artwork with the same four tile
// rotations.
//
// The Zen/Topo base-art turn still applies on top of this - their artwork
// is drawn turned relative to the photo, and that correction is
// independent of the grid's turn.

// The rotation the card draws a shape at when the option names an orientation
// the shape has no matching child for.
//
// The option labels ARE absolute orientations (the shape's own rotation and
// its children's), so the shape is drawn at that angle. On the 45° grid the
// labels are the authored orientation less the grid's own 45° turn, so the
// turn is added back here.
const previewRotation = (deg: number): number => {
  const shape = gridStore.selectedShape
  const flip = engravedBaseArtFlip(shape?.prefix)
  const orientation = is45degGrid.value ? deg + GRID_TURN_45DEG : deg
  return norm(orientation + flip)
}

const setRotation = (deg: number) => {
  if (gridStore.selectedItemsOnGrid.length > 0) {
    const rotation = is45degGrid.value ? deg + GRID_TURN_45DEG : deg
    interactions.changeShapeRotation({ target: { value: String(rotation) } } as unknown as Event)
  } else {
    // The option may name a different SHAPE, not just a different angle: the
    // halflaps and shiplaps list a child that is a different shape (the
    // horizontal variant), and the desktop picker selects that child outright
    // (components/NewStudio/logic.ts: handleShapeClick(child)). Setting only
    // the rotation left the parent as the selected shape, so picking the
    // horizontal variant still placed the vertical one.
    //
    // The rotation the store applies is the user's chosen angle (0, 90, 180,
    // 270); the store's setTempItemByCurrentSelection (lines 777-780) adds
    // the +45 grid turn itself when placing on the 45° grid.
    const variant = previewVariantForAngle(deg).shape
    if (variant && variant.id !== gridStore.selectedShape?.id) {
      interactions.handleShapeClick(variant)
    }
    gridStore.setSelectedRotation(deg)
    gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD)
    gridStore.setTempItemByCurrentSelection()
  }
  // Keep the rotation card open so the user can verify or pick a different
  // rotation before placing the tile. The user dismisses via Back or by
  // tapping a non-rotatable shape.
}

const backToStrip = () => {
  pickerMode.value = 'strip'
}

watch(patternId, (grid) => {
  // Don't auto-switch off a tab just because the grid changed — all four
  // core tabs are visible on every grid. The per-shape availability flag
  // greys out tiles that don't apply on the new grid context.
  pickerMode.value = 'strip'

  // The 3D grid is the exception: its Shapes tab has no pickable tiles at
  // all (the 2D tiles are reference-only there), so landing on it shows a
  // strip of greyed-out shapes. The 3D grid's own shapes live in the Boxes
  // tab, so go straight there.
  if (grid === '3d') {
    activeTab.value = 'boxes'
  }
})

</script>

<style scoped>
/* The link word in the availability label ("grids" / "3D Grid"). Mirrors the
   desktop's .shape-picker__grid-link: the studio navy, bold, underlined. */
.availability-link {
  color: #313558;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.active-tab {
  position: relative;
}
.active-tab::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -6px;
  height: 2px;
  background: #313558;
  border-radius: 2px;
}

/* Storyboard & Developer Note CSS from Figma 7374:204715

   The right-edge white fade only makes sense when there IS content to scroll
   off the edge. On a strip that fits on screen (Wooden Slats) it covered the
   last tile's right end and read as clipped content, so it is scoped to the
   scrollable case. */
.strip-wrap {
  position: relative;
}
.strip-wrap--can-scroll::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 32px;
  background: linear-gradient(to right, transparent, #FFFFFF);
  pointer-events: none;
  z-index: 3;
}
/* The tile strip is a fixed 115px on every tab and every grid, so the bottom
   section never changes height as the user switches tabs or grids.

   115 is the spec height from Figma 7605:179044 ("Square Grid" strip instance,
   360×115) and matches the strip the other tabs already use.

   The Boxes tab previously got its own 148px (.strip-wrap--boxes) because its
   3D thumbnails are tall — the 36"×36 cube renders at 88×107, and with the
   label stacked above it the tallest tile needs ~165px. At the shared 115px the
   box tiles are clipped from the bottom; they still scroll horizontally and
   the tile buttons keep their full height, so the labels stay legible. If the
   Boxes tab needs its full artwork back, the fix is to shrink the 3D display
   sizes rather than to re-introduce a per-tab height. */

/* Figma 7671:233687 — the wood slat icon INSTANCE carries a 90° rotation
   (`rotation: 1.5707963705062848`), which is what turns the portrait
   source SVG (52×104) into the horizontal slat bar the spec shows.
   Instance bounds are 87.45×43.73 (2x4) and 174.9×43.73 (2x8).

   The wrapper reserves the rotated (landscape) footprint so flex gives each
   tile its Figma width. Inside it, WoodSlatPickerIcon is sized to the
   *un-rotated* portrait box (44 wide × --slat-w tall) and rotated 90° about
   its own centre, so the painted slat exactly fills the landscape wrapper.

   The icon paints itself with background-image + `background-size: 100% 100%`
   rather than an <img>. That matters: an <img> rendering this SVG would keep
   its intrinsic 52×104 aspect ratio (preserveAspectRatio defaults to
   xMidYMid meet) and letterbox inside the 44×175 box the 2x8 tile asks for,
   which is why the 2x8 slat used to paint at the same width as the 2x4. A
   stretched background fills any target box, so 2x4 and 2x8 render at their
   true 88px / 175px widths.

   Only the box model and the rotation live here; the component owns the
   default / hover / selected / disabled artwork swap. */
.wood-slat-icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  line-height: 0;
  overflow: hidden;
}
.wood-slat-tile .wood-slat-icon-box :deep(.wood-slat-picker-icon) {
  display: block;
  flex: none;
  line-height: 0;
  width: 44px;
  height: var(--slat-w, 88px);
  transform: rotate(90deg);
  transform-origin: 50% 50%;
}

.tile-group {
  position: relative;
}
.tile-group-header {
  position: sticky;
  left: 15px;
  z-index: 4;
  background: #FFFFFF;
  padding-right: 6px;
  width: max-content;
}

.scroller-content {
  /* The shape strip only scrolls horizontally. Never allow vertical overflow
     or a vertical scrollbar to appear in the 99px strip. */
  overflow-y: hidden !important;
}

.scroll-fade {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 24px;
  z-index: 2;
  pointer-events: none;
}
.scroll-fade--left {
  left: 0;
  background: linear-gradient(to right, #FFFFFF, transparent);
}
.scroll-fade--right {
  right: 0;
  background: linear-gradient(to left, #FFFFFF, transparent);
}

.tile-btn {
  /* Sized per-shape via FONT_TILE_FRAME_WIDTH matching Figma node 7605:173261.
     Each tile uses 0 horizontal padding so the 8px flex-gap (.gap-2) is
     the exact distance between tile frames, keeping spacing tight and
     faithful to the design spec. */
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  padding: 1px 0 2px;
  border: 0;
  background: transparent;
  transition: all 0.15s ease;
  touch-action: manipulation;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: none;
  overflow: visible;
  position: relative;
  min-width: 0;
}

.tile-btn:active {
  transform: scale(0.95);
}

.tile-btn.active {
  background: transparent;
  box-shadow: none;
}

/* Unavailable state — mirrors the desktop's `.boxes-blank__shape--unavailable`
   (panelContract.ts:90) and `.shape-picker__grid--unavailable`
   (ShapePicker.vue:371). Tiles with `unavailable: true` render with a
   solid greyed-out look matching desktop and `cursor: not-allowed`. */
.tile-btn--unavailable {
  cursor: not-allowed;
  pointer-events: none;
  box-shadow: none;
}
.tile-btn--unavailable .tile-btn-label {
  color: #9A9994;
}

/* 2D shapes (Shapes tab): fill with #CFCECA grey and #8F8E89 stroke, matching
   desktop (ShapePicker.vue's `button:disabled :deep(path)` rule). The
   desktop rule does not spare the filled variants, so neither does this —
   every disabled tile is painted grey here. */
.tile-btn--unavailable .tile-btn-icon :deep(svg path),
.tile-btn--unavailable .tile-btn-icon :deep(svg rect),
.tile-btn--unavailable .tile-btn-icon :deep(svg polygon),
.tile-btn--unavailable .tile-btn-icon :deep(svg circle),
.tile-btn--unavailable .tile-btn-icon :deep(svg ellipse),
.tile-btn--unavailable .tile-btn-icon :deep(svg line),
.tile-btn--unavailable .tile-btn-icon :deep(svg:not(.shape-svg--filled) path),
.tile-btn--unavailable .tile-btn-icon :deep(svg:not(.shape-svg--filled) rect),
.tile-btn--unavailable .tile-btn-icon :deep(svg:not(.shape-svg--filled) polygon),
.tile-btn--unavailable .tile-btn-icon :deep(svg:not(.shape-svg--filled) circle),
.tile-btn--unavailable .tile-btn-icon :deep(svg:not(.shape-svg--filled) ellipse),
.tile-btn--unavailable .tile-btn-icon :deep(svg:not(.shape-svg--filled) line) {
  fill: #CFCECA !important;
  stroke: #8F8E89 !important;
  stroke-width: 1.33px !important;
}
.tile-btn--unavailable .tile-btn-icon :deep(svg) {
  background: transparent !important;
}

/* 3D Boxes: fill background with #D3D2CE, matching desktop .boxes-blank__shape--unavailable */
.boxes-tab .tile-btn--unavailable .tile-btn-icon,
.strip-wrap[data-tab="boxes"] .tile-btn--unavailable .tile-btn-icon {
  background-color: #D3D2CE;
  border-radius: 2px;
}
.boxes-tab .tile-btn--unavailable .tile-btn-3d-svg,
.strip-wrap[data-tab="boxes"] .tile-btn--unavailable .tile-btn-3d-svg {
  background-color: #D3D2CE;
  border-radius: 2px;
}

/* Engraved & Printed tiles: fill background with #CFCECA, matching desktop */
.strip-wrap[data-tab="engraved"] .tile-btn--unavailable .tile-btn-icon,
.strip-wrap[data-tab="printed"] .tile-btn--unavailable .tile-btn-icon {
  background-color: #CFCECA;
  border-radius: 2px;
}

/* Printed patterns keep their artwork visible as line work on the grey
   ground, exactly as desktop does (ShapePicker.vue's
   `[data-shape-id^="Geo"]:disabled :deep(path)` family): transparent fill
   and a thin grey stroke instead of a solid grey block. */
.strip-wrap[data-tab="printed"] .tile-btn--unavailable .tile-btn-icon :deep(svg path),
.strip-wrap[data-tab="printed"] .tile-btn--unavailable .tile-btn-icon :deep(svg rect),
.strip-wrap[data-tab="printed"] .tile-btn--unavailable .tile-btn-icon :deep(svg polygon),
.strip-wrap[data-tab="printed"] .tile-btn--unavailable .tile-btn-icon :deep(svg circle),
.strip-wrap[data-tab="printed"] .tile-btn--unavailable .tile-btn-icon :deep(svg ellipse),
.strip-wrap[data-tab="printed"] .tile-btn--unavailable .tile-btn-icon :deep(svg line) {
  fill: transparent !important;
  stroke: #8F8E89 !important;
  stroke-width: 0.4px !important;
}

.tile-btn-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 9px;
  font-weight: 500;
  line-height: 11px;
  text-align: center;
  color: #1C1C1E;
  max-width: 56px;
  /* display: flex would overwrite display: -webkit-box and break the 2-line
     clamp, letting 3-line labels (Large Parallel, Large Center Peak) push
     the tile past the strip's 99px and trigger a vertical scrollbar. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.01em;
  min-height: 11px;
}

/* The 45° grid's labels are wider, so they stay on ONE line.

   On this grid every tile's artwork is turned 45°, which grows its painted
   bounding box to (w + h) / sqrt(2) in both directions - the Large Square's
   50x50 art becomes a 70.7x70.7 diamond. The label sits above the artwork,
   so a label that wraps to two lines pushes the artwork down by another
   11px, and the diamond's lower corner runs off the bottom of the strip.

   Figma gives the 45° variant wider labels for exactly this reason: the
   Large Square's label is 63px wide (and 78px for the Large Half Circle),
   where the square-grid variant's is narrower. Widening the label to 78px
   here keeps every one of them on a single line, which is what the spec
   shows, and gives the artwork the vertical room it needs.

   Only the 45° grid is affected: on the others the artwork is not turned,
   so the label height does not push it into anything. */
.strip-wrap--45deg .tile-btn-label {
  max-width: 78px;
  line-height: 10px;
  min-height: 10px;
}

.strip-wrap--45deg .tile-group-header {
  height: 14px;
}

.strip-wrap--45deg .header-chip {
  height: 14px;
  padding-left: 5px;
  padding-right: 5px;
  font-size: 10px;
  line-height: 12px;
}

.strip-wrap--45deg .tile-btn {
  gap: 1px;
}

.tile-btn-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  flex-shrink: 0;
}

.tile-btn-icon-svg {
  display: inline-block;
  transform-origin: bottom center;
  line-height: 0;
}

/* 3D-perspective thumbnails rendered from /new-studio/shapes/3d/**
   and /new-studio/shapes/3d-engraved/**. Display sizes match the
   desktop shape dimensions (CubeLarge 82px, CubeSmall 32px, Rect 32×82).
   We size them slightly larger in the picker so they read clearly. */
/* 3D box thumbnails (per Figma 7282:204098): each tile is sized to its
   physical size category — 12"×12" cube 44×57, 12"×36" rect 38×104,
   36"×36" cube 88×107 — via inline width/height from
   getShapeDisplaySize(). `object-fit: contain` keeps the perspective art
   undistorted inside that footprint. */
.tile-btn-3d-svg {
  display: block;
  flex: none;
  object-fit: contain;
  transform-origin: bottom center;
  pointer-events: none;
}

.tile-btn-icon :deep(svg) {
  display: block;
  max-width: 100%;
  max-height: 100%;
  transform-origin: bottom center;
}

/* The half circles are drawn at the artwork's own (horizontal) aspect
   ratio and turned upright with a CSS rotation, so the SVG element is
   WIDER than the footprint the tile reserves - 50x25 inside a 25x50 slot.

   The clamp above cannot apply to a rotated tile: it squeezed the SVG
   back into the slot, which letterboxed the artwork to half its height
   again - the very bug this sizing is meant to fix. The SVG has to be
   free to be as wide as its artwork before the rotation turns it.

   The rotation is about the element's centre, and the element is centred
   in the reserved footprint, so the artwork still lands exactly on it - it
   just overflows the SVG's own box while rotated, which is what we want. */
.tile-btn-icon--rotated :deep(svg) {
  max-width: none;
  max-height: none;
}

/* Outline treatment for tiles whose SVG has no own fill/stroke to preserve.
   Figma 4506:96579 draws every tile edge at the same weight — measuring the
   rendered node gives ~1.33px, uniform across all shapes. Shapes routed
   through .shape-svg--filled keep their own stroke-width="2", which with
   vector-effect:non-scaling-stroke painted at 2px and read as noticeably
   heavier next to these. Both groups now use 1.33px and the same grey. */
/* `line` is in these lists for completeness. Note that a stroked shape with
   a zero-width bounding box (a <line>, or a <path> whose d is a single
   vertical segment) is culled by the browser when vector-effect:non-scaling
   -stroke is in play - which is why Large Parallel's parallel rules are
   closed paths in the SVG rather than open vertical ones. */
.tile-btn:not(.tile-btn--unavailable) .tile-btn-icon :deep(svg:not(.shape-svg--filled) path),
.tile-btn:not(.tile-btn--unavailable) .tile-btn-icon :deep(svg:not(.shape-svg--filled) rect),
.tile-btn:not(.tile-btn--unavailable) .tile-btn-icon :deep(svg:not(.shape-svg--filled) polygon),
.tile-btn:not(.tile-btn--unavailable) .tile-btn-icon :deep(svg:not(.shape-svg--filled) circle),
.tile-btn:not(.tile-btn--unavailable) .tile-btn-icon :deep(svg:not(.shape-svg--filled) ellipse),
.tile-btn:not(.tile-btn--unavailable) .tile-btn-icon :deep(svg:not(.shape-svg--filled) line),
.rotation-card-wrap :deep(svg:not(.shape-svg--filled) path),
.rotation-card-wrap :deep(svg:not(.shape-svg--filled) rect),
.rotation-card-wrap :deep(svg:not(.shape-svg--filled) polygon),
.rotation-card-wrap :deep(svg:not(.shape-svg--filled) circle),
.rotation-card-wrap :deep(svg:not(.shape-svg--filled) ellipse),
.rotation-card-wrap :deep(svg:not(.shape-svg--filled) line) {
  fill: transparent !important;
  stroke: #8F8E89 !important;
  stroke-width: 1.33px !important;
  vector-effect: non-scaling-stroke !important;
}

/* Bring the filled group to the same weight AND colour so the strip reads
   uniformly. Figma 4506:96579 draws every tile edge in #8F8E89; the filled
   SVGs ship stroke="#000", which rendered as visibly darker/heavier lines
   next to the grey outline tiles. `.printed-tile` is excluded — printed
   patterns keep their own artwork. */
.tile-btn-icon:not(.printed-tile) :deep(svg.shape-svg--filled path),
.tile-btn-icon:not(.printed-tile) :deep(svg.shape-svg--filled rect),
.tile-btn-icon:not(.printed-tile) :deep(svg.shape-svg--filled polygon),
.tile-btn-icon:not(.printed-tile) :deep(svg.shape-svg--filled circle),
.tile-btn-icon:not(.printed-tile) :deep(svg.shape-svg--filled ellipse),
.tile-btn-icon:not(.printed-tile) :deep(svg.shape-svg--filled line),
.rotation-card-wrap:not(.printed-card) :deep(svg.shape-svg--filled path),
.rotation-card-wrap:not(.printed-card) :deep(svg.shape-svg--filled rect),
.rotation-card-wrap:not(.printed-card) :deep(svg.shape-svg--filled polygon),
.rotation-card-wrap:not(.printed-card) :deep(svg.shape-svg--filled circle),
.rotation-card-wrap:not(.printed-card) :deep(svg.shape-svg--filled ellipse),
.rotation-card-wrap:not(.printed-card) :deep(svg.shape-svg--filled line) {
  stroke: #8F8E89 !important;
  stroke-width: 1.33px !important;
  vector-effect: non-scaling-stroke !important;
}

/* The active tile fills its artwork with an opaque colour. */
.tile-btn.active .tile-btn-icon :deep(svg:not(.shape-svg--filled) path),
.tile-btn.active .tile-btn-icon :deep(svg:not(.shape-svg--filled) rect),
.tile-btn.active .tile-btn-icon :deep(svg:not(.shape-svg--filled) polygon),
.tile-btn.active .tile-btn-icon :deep(svg:not(.shape-svg--filled) circle),
.tile-btn.active .tile-btn-icon :deep(svg:not(.shape-svg--filled) ellipse) {
  fill: #CCD5DA !important;
  stroke: #313558 !important;
}


/* Active state for filled printed patterns (Aspen, Bamboo, Pinstripe,
   Blossom, Strand): use opacity to indicate selection while preserving
   the SVG's original colors. The base outline rule above uses
   :not(.shape-svg--filled) so filled SVGs are not forced into the
   gray-outline style. */
.tile-btn.active .tile-btn-icon :deep(svg.shape-svg--filled) {
  opacity: 0.7;
}

.picker-swap-enter-active,
.picker-swap-leave-active {
  transition: opacity 200ms ease;
}
.picker-swap-enter-from,
.picker-swap-leave-to {
  opacity: 0;
}

/* Rotation-card switch (Figma 7675:162616).

   Tapping a rotatable tile grows the panel from 168px to 223px so the
   card's header lands where the edit toolbar was and its rotation tiles
   land where the shape strip was. That only reads as motion if the banner
   and category tabs get out of the way on the same beat, so they collapse
   by height (not v-show, which unmounts them instantly) with a matching
   300ms ease-out, fading out on the way down and back in on the way up.

   The card and strip cross-fade on the same schedule. The swap used to run
   out-then-in sequentially (400ms) while the layout snapped around it,
   which is what made the switch feel janky. */
.rotation-mode .currently-selected-banner {
  height: 0;
  opacity: 0;
  overflow: hidden;
  transition: height 300ms ease-out, opacity 200ms ease-out;
}
.currently-selected-banner {
  transition: height 300ms ease-out, opacity 200ms ease-out 100ms;
}

.rotation-mode .section-tabs-strip {
  height: 0;
  padding-top: 0;
  padding-bottom: 0;
  border-bottom-width: 0;
  opacity: 0;
  overflow: hidden;
  border-bottom-color: transparent;
  transition: height 300ms ease-out, padding 300ms ease-out, opacity 200ms ease-out, border-color 300ms ease-out;
}
.section-tabs-strip {
  transition: height 300ms ease-out, padding 300ms ease-out, opacity 200ms ease-out 100ms, border-color 300ms ease-out;
}

/* The section grows 168 -> 223px on the same 300ms track as the sheet
   band (see .sheet-band--picker in MobileTools/index.vue); without this the
   class swap snaps instantly and the taller panel overflows its
   still-animating band mid-transition. */
.mobile-shape-panel {
  transition: height 300ms ease-out;
}

/* Tight sequential fade, total 300ms so the swap lands exactly when the
   height animation settles. No enter delay — the old 80ms pause left the
   card invisible at opacity 0 after the strip was already gone. */
.picker-swap-enter-active {
  transition: opacity 190ms ease-out;
}
.picker-swap-leave-active {
  transition: opacity 110ms ease-in;
}

@media (prefers-reduced-motion: reduce) {
  .mobile-shape-panel,
  .rotation-mode .currently-selected-banner,
  .currently-selected-banner,
  .rotation-mode .section-tabs-strip,
  .section-tabs-strip {
    transition: none;
  }
}
</style>
